/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, User, Mail, Phone, MapPin, Building, Briefcase, Send, Pencil, Save, X, Loader2, Trash2, FlaskConical, Star, ArrowRight, Share2, Upload, FileText, AlertTriangle, CheckCircle, ShieldOff, UserPlus, Search, Bookmark, Linkedin, FolderPlus, ChevronDown } from 'lucide-react';
import SentEmailDialog from '../components/SentEmailDialog';
import PharmaEnrichButton from '../components/PharmaEnrichButton';
import { InterestTagger } from '../components/InterestTagger';
import { StructuredInterestCards } from '../components/StructuredInterestCards';
import BulkSendProgress from '@/components/BulkSendProgress';
import { CopyEmail } from '@/components/CopyEmail';
import { CountrySelect } from '@/components/CountrySelect';
import { TherapeuticAreaSelect } from '@/components/TherapeuticAreaSelect';
import { DiseaseAreaSelect } from '@/components/DiseaseAreaSelect';
import { toast } from 'sonner';

const API = '/api';

const OUTREACH_COLORS = {
  'Pipeline': 'bg-slate-100 text-slate-600',
  'Active': 'bg-sky-100 text-sky-700',
  'Introduction Made': 'bg-cyan-100 text-cyan-700',
  'Email Acknowledged': 'bg-lime-100 text-lime-700',
  'In Negotiation': 'bg-purple-100 text-purple-700',
  'Asset Under Evaluation': 'bg-amber-100 text-amber-700',
  'Meeting Requested': 'bg-violet-50 text-violet-600',
  'Meeting Scheduled': 'bg-violet-100 text-violet-700',
  'CDA in Progress': 'bg-blue-50 text-blue-600',
  'CDA Signed': 'bg-blue-100 text-blue-700',
  'Due Diligence': 'bg-indigo-100 text-indigo-700',
  'Deal Discussion': 'bg-purple-100 text-purple-700',
  'Closed - Successful': 'bg-emerald-100 text-emerald-700',
  'Declined': 'bg-rose-100 text-rose-700',
  'Awaiting Trial Data': 'bg-orange-100 text-orange-700',
};

const COMPANY_TYPES = ['Big Pharma', 'Mid Pharma', 'Specialty Pharma', 'Biotech', 'CRO', 'CDMO', 'Other'];

// FastAPI returns 422 validation errors as an array of objects
// ({type, loc, msg, input, url}) — React can't render those directly.
// Normalise any axios error shape down to a plain string for toast/UI use.
const errMsg = (err, fallback = 'Request failed') => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join('; ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return fallback;
};

// Aug 2026 · Reusable chip-list editor for open-ended taxonomy fields
// (Specific Indications, Scientific Preferences, Priority Targets/MOAs,
// Excluded Modalities, Excluded Indications). Each accepts Enter or comma
// to commit the current input as a chip; Backspace on empty input removes
// the last chip; clicking × on a chip removes it individually.
const ChipListField = ({ label, hint, value = [], onChange, chipClass, labelClass, testId }) => {
  const [input, setInput] = React.useState('');
  const commit = () => {
    const v = (input || '').trim();
    if (!v) return;
    if (value.map((x) => x.toLowerCase()).includes(v.toLowerCase())) {
      setInput('');
      return;
    }
    onChange([...value, v]);
    setInput('');
  };
  const removeAt = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !input && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };
  return (
    <div data-testid={testId}>
      <Label className={`text-xs ${labelClass || 'text-slate-500'}`}>{label}</Label>
      <div className="mt-1 flex flex-wrap gap-1.5 border border-slate-200 rounded-md px-2 py-1.5 min-h-[42px] bg-white focus-within:border-violet-400">
        {value.map((v, i) => (
          <span
            key={i}
            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${chipClass || 'bg-slate-50 text-slate-700 border-slate-200'}`}
            data-testid={`${testId}-chip-${i}`}
          >
            {v}
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="text-slate-400 hover:text-rose-500"
              aria-label={`Remove ${v}`}
              data-testid={`${testId}-chip-${i}-remove`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commit}
          placeholder={value.length === 0 ? 'Type and press Enter…' : ''}
          className="flex-1 min-w-[120px] text-xs outline-none bg-transparent"
          data-testid={`${testId}-input`}
        />
      </div>
      {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
};

export default function PharmaContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  // Aug 2026 · parent-pharma company-level stage gate (fallback when the
  // contact has no per-person preferred_stages). Fetched alongside the
  // contact record; drives the "Company gate · Phase 3+" hint.
  const [companyStageGate, setCompanyStageGate] = useState({ company_name: '', min_stage: '', note: '' });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Do-Not-Contact + Reassign Evaluations ────────────────────────────────
  // Single dialog combines both actions because they're almost always done
  // together (e.g. Brittany asked to be removed and redirected to Jesse —
  // we block Brittany AND move her 5 evaluations to Jesse in one go).
  const [dncDialogOpen, setDncDialogOpen] = useState(false);
  const [dncSaving, setDncSaving] = useState(false);
  const [dncReason, setDncReason] = useState('');
  const [reassignTargetSearch, setReassignTargetSearch] = useState('');
  const [reassignTargetResults, setReassignTargetResults] = useState([]);
  const [reassignTargetPicked, setReassignTargetPicked] = useState(null);
  const [reassignRunning, setReassignRunning] = useState(false);
  const [viewEmailRecord, setViewEmailRecord] = useState(null);
  const [form, setForm] = useState({});
  const [interestInput, setInterestInput] = useState('');
  const [companyNames, setCompanyNames] = useState([]);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  // Aug 2026 · "Signals only" filter — defaults ON when a positive company
  // signal is worded as exclusive ("exclusively focusing on ...").
  const [signalsOnly, setSignalsOnly] = useState(false);
  useEffect(() => {
    setSignalsOnly(Boolean(matchResults?.company_signals?.exclusive));
  }, [matchResults?.company_signals?.exclusive]);
  const signalMatchCount = (matchResults?.matches || []).filter(m => (m.signal_boost || []).length > 0).length;
  const displayedMatches = (matchResults?.matches || []).filter(m => !signalsOnly || (m.signal_boost || []).length > 0);
  // Create-Account-from-contact quick-spin workflow
  const [createAccountOpen, setCreateAccountOpen] = useState(false);
  const [createAccountSaving, setCreateAccountSaving] = useState(false);
  const [accountForm, setAccountForm] = useState({
    project_name: '', therapeutic_areas: [], preferred_stages: [], preferred_modalities: [], preferred_diseases: [], notes: '',
  });
  const [taChipInput, setTaChipInput] = useState('');
  const [stageChipInput, setStageChipInput] = useState('');
  const [modalityChipInput, setModalityChipInput] = useState('');
  const [diseaseChipInput, setDiseaseChipInput] = useState('');
  const [diseaseLoading, setDiseaseLoading] = useState(false);
  const [spawnedAccounts, setSpawnedAccounts] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  // Introduction email state
  const [introOpen, setIntroOpen] = useState(false);
  const [introSubject, setIntroSubject] = useState('');
  const [introBody, setIntroBody] = useState('');
  const [introSending, setIntroSending] = useState(false);
  const [introExcludedPartners, setIntroExcludedPartners] = useState([]);
  const [introExcludedDnc, setIntroExcludedDnc] = useState([]);
  const [includedPartnerIds, setIncludedPartnerIds] = useState(new Set());
  const [introGenerating, setIntroGenerating] = useState(false);
  const [introTaskId, setIntroTaskId] = useState(null);
  const [introProgress, setIntroProgress] = useState(null);
  const [introEmailTargets, setIntroEmailTargets] = useState([]);
  const [introPreviews, setIntroPreviews] = useState(null);
  const [introPreviewLoading, setIntroPreviewLoading] = useState(false);
  const [introTestSentTo, setIntroTestSentTo] = useState('');
  const [introRecipientPreview, setIntroRecipientPreview] = useState(null);
  const [introRecipientLoading, setIntroRecipientLoading] = useState(false);
  const [introRecipientExpanded, setIntroRecipientExpanded] = useState(false);
  // ─── Campaign engine (idempotent + resumable) ────────────────────────
  // Persistent state that survives backend restarts. `campaignPlan` is the
  // pre-send breakdown (already-sent / previously-failed / remaining) so
  // the modal can show accurate counts BEFORE the user hits Send.
  // `campaignId` + `campaignProgress` drive the progress bar via the
  // /outreach-campaigns/{id} endpoint instead of the ephemeral
  // /bulk-email/status/{task_id} tracker (which was wiped on hot reload
  // and caused the "stuck at 85" bug).
  const [campaignPlan, setCampaignPlan] = useState(null);
  const [campaignPlanLoading, setCampaignPlanLoading] = useState(false);
  const [campaignId, setCampaignId] = useState(null);
  const [campaignProgress, setCampaignProgress] = useState(null);
  const [historicalCampaigns, setHistoricalCampaigns] = useState([]);
  const [viewSentOpen, setViewSentOpen] = useState(false);

  // When the intro dialog opens, resolve which biotech contact will actually
  // receive the email per company (BD-aware primary picker on the backend).
  // Lets the user audit "Will go to: David Liu, VP BD" per row before Send.
  useEffect(() => {
    if (!introOpen) return;
    const targets = introEmailTargets.length ? introEmailTargets : (matchResults?.matches || []);
    if (!targets.length) return;
    setIntroRecipientLoading(true);
    axios.post(
      `${API}/bulk-email/introduction-outreach/recipients`,
      {
        matches: targets,
        include_active_partner_ids: Array.from(includedPartnerIds || []),
      },
      { withCredentials: true }
    )
      .then(r => setIntroRecipientPreview(r.data))
      .catch(() => setIntroRecipientPreview(null))
      .finally(() => setIntroRecipientLoading(false));
  }, [introOpen, introEmailTargets, matchResults, includedPartnerIds]);
  const [deckUploading, setDeckUploading] = useState(false);
  // ─── Quick-create an account from this contact's preferences ────────────
  // Pulls TAs, stages, modalities from the contact + their company's
  // MOAT-enriched profile. Auto-names "{Pharma} {Primary TA} In-Licensing"
  // so the user doesn't have to manually rename in the accounts list.
  const fetchSpawnedAccounts = async () => {
    if (!id) return;
    try {
      const res = await axios.get(
        `${API}/india-licensing/by-contact/${id}`,
        { withCredentials: true },
      );
      setSpawnedAccounts(res.data.items || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { fetchSpawnedAccounts(); /* eslint-disable-next-line */ }, [id]);

  const openCreateAccountDialog = () => {
    if (!contact) return;
    const tas = (contact.interest_areas || []).filter(Boolean);
    const stages = (contact.preferred_stages || []).filter(Boolean);
    const modalities = (contact.preferred_modalities || []).filter(Boolean);
    const primaryTA = tas[0] || '';
    const autoName = [contact.company_name, primaryTA ? primaryTA.replace(/\b\w/g, c => c.toUpperCase()) : '', 'In-Licensing'].filter(Boolean).join(' ');
    setAccountForm({
      project_name: autoName,
      therapeutic_areas: tas,
      preferred_stages: stages,
      preferred_modalities: modalities,
      preferred_diseases: [],
      notes: '',
    });
    setTaChipInput('');
    setStageChipInput('');
    setModalityChipInput('');
    setDiseaseChipInput('');
    setCreateAccountOpen(true);
    // Auto-derive diseases from the pre-filled TAs so the user doesn't
    // have to type them. Falls back silently on error — the user can
    // always type them manually.
    if (tas.length > 0) {
      setDiseaseLoading(true);
      axios.post(`${API}/india-licensing/suggest-diseases`,
        { therapeutic_areas: tas },
        { withCredentials: true })
        .then(r => setAccountForm(prev => ({ ...prev, preferred_diseases: r.data?.diseases || [] })))
        .catch(() => {})
        .finally(() => setDiseaseLoading(false));
    }
  };

  const handleCreateAccount = async () => {
    if (!accountForm.project_name?.trim()) {
      toast.error('Account name is required');
      return;
    }
    setCreateAccountSaving(true);
    try {
      const res = await axios.post(`${API}/india-licensing/from-pharma-contact`, {
        contact_id: id,
        name_override: accountForm.project_name.trim(),
        ta_overrides: accountForm.therapeutic_areas,
        stage_overrides: accountForm.preferred_stages,
        modality_overrides: accountForm.preferred_modalities,
        disease_overrides: accountForm.preferred_diseases,
        notes: accountForm.notes,
      }, { withCredentials: true });
      toast.success(`Account created — opening "${res.data.project_name}"`);
      setCreateAccountOpen(false);
      fetchSpawnedAccounts(); // refresh inline list (in case user comes back)
      navigate(`/accounts/${res.data.project_id}`);
    } catch (err) {
      toast.error(errMsg(err, 'Failed to create account'));
    } finally {
      setCreateAccountSaving(false);
    }
  };

  const addChip = (field, value, setInput) => {
    const v = (value || '').trim();
    if (!v) return;
    setAccountForm(prev => {
      if (prev[field].some(x => x.toLowerCase() === v.toLowerCase())) return prev;
      const next = { ...prev, [field]: [...prev[field], v] };
      // When the TA chip set changes, re-derive the diseases so they
      // track the TA. Disease chips themselves don't trigger a re-derive.
      if (field === 'therapeutic_areas') {
        setDiseaseLoading(true);
        axios.post(`${API}/india-licensing/suggest-diseases`,
          { therapeutic_areas: next.therapeutic_areas },
          { withCredentials: true })
          .then(r => setAccountForm(p => ({ ...p, preferred_diseases: r.data?.diseases || [] })))
          .catch(() => {})
          .finally(() => setDiseaseLoading(false));
      }
      return next;
    });
    setInput('');
  };

  const removeChip = (field, value) => {
    setAccountForm(prev => ({ ...prev, [field]: prev[field].filter(x => x !== value) }));
  };


  const handleShareMatchLink = async () => {
    setShareLoading(true);
    try {
      const res = await axios.post(`${API}/share/contact-matches/${id}`, {}, { withCredentials: true });
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/matches/${res.data.share_token}`;
      await navigator.clipboard.writeText(url);
      window.open(url, '_blank');
      toast.success(`Link copied & opened in new tab`);
    } catch (err) {
      toast.error(errMsg(err, 'Failed to generate link'));
    } finally { setShareLoading(false); }
  };

  const handleDeckUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['.pdf', '.ppt', '.pptx'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowed.includes(ext)) { toast.error('Only PDF, PPT, and PPTX files are allowed'); return; }
    setDeckUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await axios.post(`${API}/pharma-contacts/${id}/upload-deck`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const storedOn = resp?.data?.stored_on;
      const deduped = resp?.data?.deduped;
      if (deduped) {
        toast.success(`Linked to existing company deck (no duplicate upload) — you shared "${resp.data.deck?.filename || file.name}" with this contact.`, { duration: 8000 });
      } else if (storedOn === 'company_profile') {
        toast.success('Corporate deck uploaded to the company profile — visible across all contacts of this account.', { duration: 8000 });
      } else {
        toast.success('Corporate deck uploaded');
      }
      fetchContact();
    } catch (err) { toast.error(errMsg(err, 'Upload failed')); }
    finally { setDeckUploading(false); e.target.value = ''; }
  };

  const handleDeleteDeck = async (filename) => {
    if (!window.confirm(`Remove "${filename}"?`)) return;
    try {
      await axios.delete(`${API}/pharma-contacts/${id}/delete-deck`, { data: { filename }, withCredentials: true });
      toast.success('Deck removed');
      fetchContact();
    } catch { toast.error('Failed to remove deck'); }
  };

  // Auto-flow when user lands here from Alerts page with the
  // `?autoMatchNonPartners=1` flag — runs Match Assets, then opens the
  // Intro Outreach dialog scoped to non-partner database biotechs only
  // (so the user can email each biotech CEO about the pharma's interest).
  useEffect(() => {
    if (!contact) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('autoMatchNonPartners') !== '1') return;
    const taFilter = params.get('ta') || '';
    window.history.replaceState({}, '', window.location.pathname);
    (async () => {
      setMatchLoading(true);
      try {
        const res = await axios.post(`${API}/ai/match-contact-assets`, { contact_id: id }, { withCredentials: true });
        await axios.post(`${API}/ai/save-matches`, { contact_id: id, matches: res.data.matches, company_signals: res.data.company_signals || null }, { withCredentials: true });
        const saved = await axios.get(`${API}/ai/saved-matches/${id}`, { withCredentials: true });
        const allMatches = saved.data.saved ? saved.data.matches : res.data.matches;
        setMatchResults({ ...res.data, matches: allMatches, saved: true });
        let nonPartners = allMatches.filter(m => !m.is_my_company && !m.is_inbound_only);
        // Optional TA scope filter (when launched from a specific bucket on
        // the Alerts page) — match if asset indication / TA contains the
        // TA name (case-insensitive).
        if (taFilter) {
          const re = new RegExp(taFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
          nonPartners = nonPartners.filter(m =>
            re.test(m.indication || '') ||
            re.test(m.therapeutic_area || '') ||
            re.test(m.modality || '')
          );
        }
        if (!nonPartners.length) {
          toast.info(`No non-partner biotech matches found in the database${taFilter ? ` for ${taFilter}` : ''}`);
          return;
        }
        toast.success(`Found ${nonPartners.length} database biotech${nonPartners.length === 1 ? '' : 's'}${taFilter ? ` in ${taFilter}` : ''} matching ${contact.name?.split(' ')[0] || 'this contact'}'s TAs`);
        // Open the intro dialog with the non-partner subset
        setIntroEmailTargets(nonPartners);
        setIntroPreviews(null);
        setIntroOpen(true);
        setIntroTaskId(null);
        setIntroProgress(null);
        if (!pharmaBrief) {
          try {
            const r = await axios.post(`${API}/bulk-email/pharma-brief`, { contact_id: id }, { withCredentials: true });
            setPharmaBrief(r.data.brief);
            setIntroBody(buildIntroTemplate(r.data.brief));
          } catch { setIntroBody(buildIntroTemplate('')); }
        } else {
          setIntroBody(buildIntroTemplate(pharmaBrief));
        }
        setIntroSubject('Licensing Opportunity — Interest in [Asset]');
      } catch (err) {
        toast.error(errMsg(err, 'Auto-match failed'));
      } finally {
        setMatchLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact]);

  const handleMatchAssets = async () => {
    setMatchLoading(true);
    try {
      const res = await axios.post(`${API}/ai/match-contact-assets`, { contact_id: id }, { withCredentials: true });
      // Save matches to DB
      await axios.post(`${API}/ai/save-matches`, { contact_id: id, matches: res.data.matches, company_signals: res.data.company_signals || null }, { withCredentials: true });
      // Reload saved to get is_new and emailed flags
      const saved = await axios.get(`${API}/ai/saved-matches/${id}`, { withCredentials: true });
      if (saved.data.saved) {
        const newCount = saved.data.matches.filter(m => m.is_new).length;
        setMatchResults({
          ...res.data,
          matches: saved.data.matches,
          saved: true,
          updated_at: saved.data.updated_at,
        });
        if (newCount > 0) toast.success(`${newCount} new match${newCount > 1 ? 'es' : ''} found!`);
        else toast.success(`${res.data.total_matches} matches refreshed`);
      } else {
        setMatchResults(res.data);
      }
    } catch (err) {
      toast.error(errMsg(err, 'Matching failed'));
    } finally { setMatchLoading(false); }
  };


  const [matchLoading, setMatchLoading] = useState(false);

  // ── Custom-prompt match (ephemeral, one-off query like J&J's "visual
  //    correction and vision restoration" email). Runs through Claude to
  //    extract keywords, then returns every asset in the DB matching those
  //    keywords. Populates the same `matchResults` state as the built-in
  //    Match button, so the existing table + bulk-email flow work unchanged.
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);
  // Jun 2026 · strict criteria toggle (OFF by default = recall-first).
  // When ON, modality becomes a hard filter (blank modality excluded) and
  // exclusions also check target/modality/description.
  const [strictMatch, setStrictMatch] = useState(false);
  const [applyingPrefs, setApplyingPrefs] = useState(false);
  const [prefsApplied, setPrefsApplied] = useState(false);

  // Union merge, case-insensitive dedupe, preserving existing order first.
  const mergeArr = (existing, incoming) => {
    const seen = new Set((existing || []).map(v => String(v).trim().toLowerCase()));
    const out = [...(existing || [])];
    (incoming || []).forEach(v => {
      const k = String(v || '').trim().toLowerCase();
      if (k && !seen.has(k)) { seen.add(k); out.push(String(v).trim()); }
    });
    return out;
  };

  // Map the extractor output → the pharma-contact profile fields.
  const detectedPrefs = (() => {
    const ex = matchResults?._source === 'prompt' ? matchResults?.extracted : null;
    if (!ex) return null;
    return {
      interest_areas: ex.therapeutic_areas || [],
      preferred_stages: ex.stages || [],
      preferred_modalities: ex.modalities || [],
      excluded_therapeutic_areas: ex.exclude || [],
    };
  })();

  const applyPrefsToProfile = async () => {
    if (!detectedPrefs) return;
    setApplyingPrefs(true);
    try {
      const merged = {
        ...form,
        interest_areas: mergeArr(form.interest_areas, detectedPrefs.interest_areas),
        preferred_stages: mergeArr(form.preferred_stages, detectedPrefs.preferred_stages),
        preferred_modalities: mergeArr(form.preferred_modalities, detectedPrefs.preferred_modalities),
        excluded_therapeutic_areas: mergeArr(form.excluded_therapeutic_areas, detectedPrefs.excluded_therapeutic_areas),
      };
      await axios.put(`${API}/pharma-contacts/${id}`, merged, { withCredentials: true });
      await fetchContact();
      setPrefsApplied(true);
      toast.success('Preferences merged into this contact\u2019s profile');
    } catch (err) {
      toast.error(errMsg(err, 'Could not apply preferences'));
    } finally {
      setApplyingPrefs(false);
    }
  };

  const handleMatchByPrompt = async () => {
    const trimmed = promptText.trim();
    if (!trimmed) { toast.error('Paste or type a prompt first'); return; }
    if (trimmed.length > 2000) { toast.error('Prompt too long (max 2000 characters)'); return; }
    setPromptLoading(true);
    try {
      const res = await axios.post(
        `${API}/ai/match-contact-assets-by-prompt`,
        { contact_id: id, prompt: trimmed, strict: strictMatch },
        { withCredentials: true }
      );
      const data = res.data || {};
      setMatchResults({
        ...data,
        matches: (data.matches || []).map(m => ({ ...m, is_new: false, emailed: false })),
        saved: false,
        _source: 'prompt',
        _prompt_text: trimmed,
      });
      setPrefsApplied(false);
      // Persist the prompt + its match set so it's restored on the next visit
      // to this contact's page (prompt "gallery" memory).
      try {
        await axios.post(`${API}/ai/save-matches`, {
          contact_id: id,
          matches: data.matches || [],
          prompt: trimmed,
          strict: strictMatch,
          extracted: data.extracted || null,
        }, { withCredentials: true });
      } catch {}
      toast.success(`${data.total_matches || 0} asset${(data.total_matches || 0) === 1 ? '' : 's'} found`);
      // Scroll the results into view once they render
      setTimeout(() => {
        document.querySelector('[data-testid="match-results-section"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    } catch (err) {
      toast.error(errMsg(err, 'Prompt search failed'));
    } finally {
      setPromptLoading(false);
    }
  };

  // Auto-scroll the prompt panel into view when opened, so users don't have to
  // hunt for it below the fold.
  const openPromptPanel = () => {
    setPromptOpen(true);
    setTimeout(() => {
      const panel = document.querySelector('[data-testid="match-by-prompt-panel"]');
      if (panel) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const ta = panel.querySelector('[data-testid="prompt-textarea"]');
        if (ta) ta.focus();
      }
    }, 80);
  };

  const [pharmaBrief, setPharmaBrief] = useState('');
  const [pharmaBriefLoading, setPharmaBriefLoading] = useState(false);

  // ── Pitch Active Partners (single email to pharma contact) ──────────────
  // Sends ONE email to the pharma contact (e.g., Andrew) listing every
  // matched asset from active-partner biotechs. Each asset row also lands
  // in the biotech's "Pharma Outreach" tab via a dedicated outreach record.
  const [pitchOpen, setPitchOpen] = useState(false);
  const [groupOverlap, setGroupOverlap] = useState(null);
  const [pitchSubject, setPitchSubject] = useState('');
  const [pitchBody, setPitchBody] = useState('');
  const [pitchAssets, setPitchAssets] = useState([]);
  const [pitchSending, setPitchSending] = useState(false);

  const buildActivePartnersBody = (assets) => {
    const recipientFirst = (contact?.name || 'there').split(' ')[0];
    const pharmaName = contact?.company_name || 'your team';
    // Normalize "Vision restoration" → "Vision Restoration" but keep "AMD",
    // "CNS", "CAR-T" intact.
    const titleCase = (s) => {
      if (!s) return s;
      return s.split(/(\s+|\/|-|—|,)/g).map(part => {
        if (!part) return part;
        if (/^\s+$/.test(part) || /^[/\-—,]$/.test(part)) return part;
        if (/^[A-Z]{2,}$/.test(part)) return part;
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }).join('');
    };
    // Build a 2-3 line narrative paragraph per asset. Prefer the curated
    // `description`, fall back to `molecule_description`. Cap at ~3 lines
    // (~420 chars) ending at a sentence boundary. If neither exists,
    // synthesize a short blurb from indication / modality / target.
    const assetParagraph = (a) => {
      const raw = (a.description || a.molecule_description || '').trim();
      if (raw) {
        if (raw.length <= 420) return raw;
        const cut = raw.slice(0, 420);
        const lastPeriod = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('? '), cut.lastIndexOf('! '));
        if (lastPeriod > 200) return raw.slice(0, lastPeriod + 1);
        const lastSpace = cut.lastIndexOf(' ');
        return raw.slice(0, lastSpace > 0 ? lastSpace : 420) + '…';
      }
      // Fallback synthesis when no description on file
      const parts = [];
      if (a.modality) parts.push(`${titleCase(a.modality)}`);
      if (a.target) parts.push(`targeting ${a.target}`);
      if (a.indication) parts.push(`for ${titleCase(a.indication)}`);
      return parts.length ? parts.join(' ') + '.' : '';
    };
    const lines = [];
    lines.push(`Dear ${recipientFirst},`);
    lines.push('');
    lines.push(
      `Following our recent conversations and your stated areas of interest, here is a first set of assets from biotechs we are already in active dialogue with — sharing them up front so you can review them at your end while I continue working through the rest of the pipeline based on your priorities.`
    );
    lines.push('');
    assets.forEach((a, i) => {
      // Heading line: "1. AL-001 — Wet AMD / DR — Phase 2 — AnlongBio Inc."
      const headParts = [`${i + 1}. ${a.asset_name || '(unnamed asset)'}`];
      if (a.indication) headParts.push(titleCase(a.indication));
      if (a.stage) headParts.push(a.stage);
      if (a.company_name) headParts.push(a.company_name);
      lines.push(headParts.join(' — '));
      lines.push('');
      const para = assetParagraph(a);
      if (para) {
        lines.push(para);
        lines.push('');
      }
    });
    lines.push(
      `If any of these look interesting and you'd like to dig deeper, I'm happy to share the non-confidential deck. We can also set up a meeting with the biotech to take the conversation forward. Once you flag the ones that catch ${pharmaName}'s eye, I'll continue circling back with the broader set as I get a clearer read on your priorities.`
    );
    // Note: signature ("Amit Dang / Founder, ForesightRx") is appended
    // automatically by the backend via EMAIL_SIGNATURE_HTML — do not add here.
    return lines.join('\n');
  };

  const openActivePartnersPitch = async (overrideAssets = null) => {
    // Feb 2026 · guard against callers that pass a SyntheticEvent
    // (e.g. `onClick={openActivePartnersPitch}` instead of
    // `onClick={() => openActivePartnersPitch()}`). Only accept a
    // real Array as the override; anything else falls back to the
    // matchResults-derived list.
    const validOverride = Array.isArray(overrideAssets) ? overrideAssets : null;
    const partnerAssets = validOverride || (matchResults?.matches || [])
      .filter(m => m.is_my_company || m.is_inbound_only)
      .map(m => ({
        company_id: m.company_id,
        company_name: m.company_name,
        asset_name: m.asset_name,
        asset_id: m.asset_id || '',
        stage: m.stage || '',
        indication: m.indication || '',
        modality: m.modality || '',
        target: m.target || '',
        platform: m.platform || '',
        description: m.description || '',
        molecule_description: m.molecule_description || '',
      }));
    if (!partnerAssets.length) { toast.info('No active-partner assets in this match list'); return; }
    if (!contact?.email) { toast.error(`No email address on file for ${contact?.name || 'this contact'}`); return; }

    // Backfill description for any asset missing it (older saved-match rows
    // didn't include description). Fetch in parallel from /api/assets/{id}.
    const needsFetch = partnerAssets.filter(a => a.asset_id && !a.description && !a.molecule_description);
    if (needsFetch.length) {
      try {
        const fetched = await Promise.all(needsFetch.map(a =>
          axios.get(`${API}/assets/${a.asset_id}`, { withCredentials: true }).then(r => r.data).catch(() => null)
        ));
        fetched.forEach((data, idx) => {
          if (!data) return;
          const a = needsFetch[idx];
          a.description = data.description || '';
          a.molecule_description = data.molecule_description || '';
        });
      } catch {}
    }

    setPitchAssets(partnerAssets);
    setPitchSubject(
      `First set of assets we're already in touch with — for your review`
    );
    setPitchBody(buildActivePartnersBody(partnerAssets));
    setGroupOverlap(null);
    axios.get(`${API}/corporate-groups/outreach-overlap/${id}`, { withCredentials: true })
      .then(r => { if (r.data?.overlap) setGroupOverlap(r.data); })
      .catch(() => {});
    setPitchOpen(true);
  };

  const sendActivePartnersPitch = async ({ test = false } = {}) => {
    if (!pitchSubject.trim() || !pitchBody.trim()) { toast.error('Subject and body are required'); return; }
    if (!pitchAssets.length) return;
    setPitchSending(true);
    try {
      const asset_keys = pitchAssets.map(a => ({
        company_id: a.company_id,
        company_name: a.company_name,
        asset_name: a.asset_name,
        asset_id: a.asset_id || '',
        stage: a.stage,
        indication: a.indication,
        modality: a.modality,
      }));
      const res = await axios.post(`${API}/bulk-email/active-partners-pitch`, {
        contact_id: id,
        subject: test ? `[TEST] ${pitchSubject}` : pitchSubject,
        email_body: pitchBody,
        asset_keys,
        test_mode: test,
      }, { withCredentials: true });
      if (test) {
        toast.success(`Test email sent to ${res.data.sent_to} (no outreach rows logged)`);
      } else {
        toast.success(`Sent to ${res.data.sent_to} — ${res.data.outreach_rows_created} outreach rows logged`);
        setPitchOpen(false);
        try {
          const saved = await axios.get(`${API}/ai/saved-matches/${id}`, { withCredentials: true });
          if (saved.data?.saved) setMatchResults(prev => ({ ...prev, matches: saved.data.matches }));
        } catch {}
      }
    } catch (err) {
      toast.error(errMsg(err, 'Failed to send pitch'));
    } finally {
      setPitchSending(false);
    }
  };

  const handleOpenIntroEmail = async (onlyNew = false, customTargets = null) => {
    if (!matchResults?.matches?.length && !customTargets) return;
    let targets;
    if (customTargets && customTargets.length) {
      targets = customTargets;
    } else if (onlyNew) {
      targets = matchResults.matches.filter(m => (m.is_new || !m.emailed) && !m.emailed_history);
    } else {
      targets = matchResults.matches;
    }
    if (!targets.length) { toast.info('All matches have already been emailed'); return; }
    setIntroEmailTargets(targets);
    setIntroPreviews(null);
    setIntroOpen(true);
    setCampaignId(null);
    setCampaignProgress(null);

    // Fetch server-side breakdown (already sent / previously failed /
    // remaining + any interrupted campaign we can resume). Runs in
    // parallel with the recipient-preview effect so the modal opens
    // instantly.
    setCampaignPlanLoading(true);
    axios.post(`${API}/outreach-campaigns/plan`, {
      contact_id: id,
      campaign_type: 'intro_outreach',
      matches: targets,
    }, { withCredentials: true })
      .then(r => setCampaignPlan(r.data))
      .catch(() => setCampaignPlan(null))
      .finally(() => setCampaignPlanLoading(false));

    // Fetch pharma brief if not cached
    if (!pharmaBrief) {
      setPharmaBriefLoading(true);
      try {
        const res = await axios.post(`${API}/bulk-email/pharma-brief`, { contact_id: id }, { withCredentials: true });
        setPharmaBrief(res.data.brief);
        setIntroBody(buildIntroTemplate(res.data.brief));
      } catch { setIntroBody(buildIntroTemplate('')); }
      finally { setPharmaBriefLoading(false); }
    } else {
      setIntroBody(buildIntroTemplate(pharmaBrief));
    }
    setIntroSubject(`Licensing Opportunity — Interest in [Asset]`);
  };

  const buildIntroTemplate = (brief) => {
    const pharmaName = contact?.company_name || 'a leading pharma company';
    // Title-case each TA so "Vision restoration" displays as "Vision
    // Restoration", matching the rest of our copy. Preserves all-caps acronyms
    // (e.g., "AMD", "CNS", "CAR-T", "IBD") by checking for >=2 consecutive uppercase.
    const titleCaseTA = (s) => {
      if (!s) return s;
      return s.split(/(\s|\/|-|—)/g).map(part => {
        if (!part || /^\s|^\/$|^-$|^—$/.test(part)) return part;
        if (/^[A-Z]{2,}$/.test(part)) return part; // acronym
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }).join('');
    };
    // For prompt-driven matches (e.g., J&J's pasted "vision restoration /
    // visual correction" email), the introduction email should reflect the
    // PROMPT's specific intent — not the contact's broad stored TAs.
    // Prefer Claude's `focus_phrase` (a tight 3-8 word capture of the user's
    // own framing, e.g., "vision restoration and visual correction"), and
    // fall back to extracted keywords only if no focus_phrase is available.
    let areasSource = contact?.interest_areas || [];
    let preserveCase = false;  // focus_phrase from prompt is kept verbatim
    if (matchResults?._source === 'prompt' && matchResults?.extracted) {
      const ex = matchResults.extracted;
      const focus = (ex.focus_phrase || '').trim();
      if (focus) {
        // Defensive lowercase: Claude sometimes returns "Visual Correction
        // And Vision Restoration" even when instructed to preserve user
        // casing. Lowercase any word that isn't a known acronym (>=2
        // consecutive uppercase like AMD, CNS, GLP-1, NASH).
        const normalized = focus.split(/(\s+|-)/g).map(part => {
          if (!part || /^\s+$/.test(part) || part === '-') return part;
          if (/^[A-Z0-9]{2,}$/.test(part)) return part;
          if (/[A-Z].*[A-Z]/.test(part)) return part; // mixed-caps brand like CAR-T
          return part.toLowerCase();
        }).join('');
        areasSource = [normalized];
        preserveCase = true;
      } else {
        const promptAreas = []
          .concat(ex.therapeutic_areas || [])
          .concat(ex.keywords || []);
        const seen = new Set();
        const picked = [];
        for (const t of promptAreas) {
          const key = String(t).trim().toLowerCase();
          if (!key || seen.has(key)) continue;
          seen.add(key);
          picked.push(String(t).trim());
          if (picked.length >= 4) break;
        }
        if (picked.length) areasSource = picked;
      }
    }
    const pharmaAreas = (preserveCase
      ? areasSource.join(', ')
      : areasSource.map(titleCaseTA).join(', ')
    ) || '[TherapeuticArea]';
    // Pull preferred modalities (from the contact's profile) so the email
    // makes the modality fit explicit, not just the TA fit. Prompt-driven
    // matches usually don't constrain by modality, so this is skipped when
    // the match was prompt-sourced.
    const pharmaMods = (matchResults?._source !== 'prompt')
      ? (contact?.preferred_modalities || []).filter(Boolean).slice(0, 4)
      : [];
    const modalityClause = pharmaMods.length
      ? `, particularly in ${pharmaMods.join(', ')}`
      : '';
    // Aug 2026 · cite the pharma's signalled focus (e.g. Bayer: "back of
    // the eye and retinal diseases") so the intro isn't just the broad TA.
    const signalAreas = (matchResults?.company_signals?.positive || []).filter(Boolean).slice(0, 3);
    const signalClause = signalAreas.length
      ? `, with a specific stated focus on ${signalAreas.join(' and ')}`
      : '';
    const briefLine = brief ? `\n\n${brief}` : '';
    // CRITICAL — never name the pharma in the outgoing intro to the biotech.
    // The biotech must not be able to identify and approach the pharma
    // directly. Use generic, neutral phrasing — the pharma is NOT
    // a ForesightRx client; we have simply identified their licensing intent.
    void pharmaName; // intentionally unused — anonymous template by design
    return `Dear [Name],

I am Dr. Amit Dang, Founder of ForesightRx (a division of MarksMan Healthcare). We track pharma licensing intent globally and connect biotechs with pharmaceutical companies that are actively scouting for in-licensing assets.${briefLine}

Based on our research, we have identified that this pharmaceutical company is actively looking for assets in ${pharmaAreas}${modalityClause}${signalClause}. Your asset [Asset] at the [Stage] stage looks like a strong fit.

If you are open to it, we would be happy to share your non-confidential deck with them on your behalf. Our model is purely success-based — no retainers, no upfront fees. We only earn if a deal materializes.

Please note: in case we are already in touch with you on another asset, you are still receiving this email because we believe [Asset] may be a separate opportunity worth exploring with this pharma. Kindly acknowledge this email or share your feedback accordingly.`;
  };

  // Pull prompt-derived keywords from current match state. When a free-text
  // prompt was used (`_source==='prompt'`), the backend should anchor the
  // outbound intro to those keywords instead of the contact's stored TAs.
  // Prefer the literal `focus_phrase` ("vision restoration and visual
  // correction") over the expanded disease keyword list.
  const promptKeywordsForIntro = () => {
    if (matchResults?._source !== 'prompt' || !matchResults?.extracted) return null;
    const ex = matchResults.extracted;
    const focus = (ex.focus_phrase || '').trim();
    if (focus) return [focus];
    const all = [].concat(ex.therapeutic_areas || []).concat(ex.keywords || []);
    const seen = new Set();
    const out = [];
    for (const t of all) {
      const k = String(t).trim().toLowerCase();
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(String(t).trim());
      if (out.length >= 5) break;
    }
    return out.length ? out : null;
  };

  const handleGenerateAIEmail = async () => {
    if (!matchResults?.matches?.length) return;
    setIntroGenerating(true);
    try {
      const sample = matchResults.matches[0];
      const res = await axios.post(`${API}/bulk-email/introduction-outreach/preview`, {
        contact_id: id,
        company_name: sample.company_name,
        assets: [sample],
        prompt_keywords: promptKeywordsForIntro(),
      }, { withCredentials: true });
      setIntroBody(res.data.email_body);
    } catch (err) {
      toast.error('Failed to generate AI email');
    } finally {
      setIntroGenerating(false);
    }
  };

  const handlePreviewTop3 = async () => {
    if (!introSubject.trim() || !introBody.trim()) {
      toast.error('Write subject and email body first');
      return;
    }
    setIntroPreviewLoading(true);
    setIntroPreviews(null);
    setIntroTestSentTo('');
    try {
      const targets = introEmailTargets.length ? introEmailTargets : matchResults.matches;
      const res = await axios.post(`${API}/bulk-email/introduction-outreach/send-test`, {
        contact_id: id,
        subject: introSubject,
        email_body: introBody,
        matches: targets,
        prompt_keywords: promptKeywordsForIntro(),
      }, { withCredentials: true });
      setIntroPreviews(res.data.results);
      setIntroTestSentTo(res.data.sent_to);
      const sentCount = res.data.results.filter(r => r.status === 'sent').length;
      toast.success(`${sentCount} test email${sentCount > 1 ? 's' : ''} sent to ${res.data.sent_to}`);
    } catch (err) {
      toast.error(errMsg(err, 'Failed to send test emails'));
    } finally {
      setIntroPreviewLoading(false);
    }
  };

  const handleSendIntroEmails = async (opts = {}) => {
    // opts.resumeCampaignId → resume an interrupted campaign instead of creating a fresh one.
    if (!opts.resumeCampaignId && (!introSubject.trim() || !introBody.trim())) {
      toast.error('Subject and email body are required');
      return;
    }
    setIntroPreviews(null);
    setIntroSending(true);
    setCampaignProgress(null);
    try {
      const targets = introEmailTargets.length ? introEmailTargets : matchResults.matches;
      const payload = {
        contact_id: id,
        campaign_type: 'intro_outreach',
        subject: introSubject,
        email_body: introBody,
        use_ai: false,
        prompt_keywords: promptKeywordsForIntro() || [],
        prompt: matchResults?._prompt_text || '',
        include_active_partner_ids: Array.from(includedPartnerIds),
        matches: targets,
      };
      if (opts.resumeCampaignId) payload.resume_campaign_id = opts.resumeCampaignId;
      const res = await axios.post(`${API}/outreach-campaigns/create`, payload, { withCredentials: true });
      const cId = res.data.campaign_id;
      setCampaignId(cId);

      const skippedCount = res.data.skipped_already_sent || 0;
      if (skippedCount > 0) {
        toast(`Skipped ${skippedCount} biotech${skippedCount === 1 ? '' : 's'} already contacted for this pharma`, {
          icon: '🛡️', duration: 6000,
        });
      }
      const plannedCount = res.data.total_planned || 0;
      toast.success(res.data.resumed
        ? `Resuming campaign — ${res.data.remaining || plannedCount} remaining…`
        : `Sending to ${plannedCount} biotech${plannedCount === 1 ? '' : 's'}…`);

      // Poll persistent campaign endpoint. Because state lives in Mongo,
      // a backend restart no longer wipes the progress — the poller
      // simply reconnects and reads the latest counts.
      let pollFailures = 0;
      const poll = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/outreach-campaigns/${cId}`, { withCredentials: true });
          pollFailures = 0;
          setCampaignProgress(s.data);
          if (s.data.status === 'completed' || s.data.status === 'failed' || s.data.status === 'interrupted') {
            clearInterval(poll);
            setIntroSending(false);
            if (s.data.status === 'completed') {
              toast.success(`Campaign complete — sent ${s.data.counts.sent}, failed ${s.data.counts.failed}`);
            } else if (s.data.status === 'interrupted') {
              toast(`Campaign interrupted — ${s.data.counts.remaining} still to send. Use Resume to continue.`, { icon: '⏸️', duration: 8000 });
            } else {
              toast.error('Campaign failed — check activity log');
            }
            // Mark matches as emailed for this contact so the table
            // reflects reality even if the user closes the modal.
            try {
              const sentBiotechIds = new Set((s.data.sent_details || []).map(x => x.biotech_company_id));
              const keys = targets
                .filter(m => sentBiotechIds.has(m.company_id))
                .map(m => `${m.company_id}|${m.asset_name}`);
              if (keys.length) {
                await axios.post(`${API}/ai/mark-matches-emailed`, { contact_id: id, emailed_keys: keys }, { withCredentials: true });
              }
              const saved = await axios.get(`${API}/ai/saved-matches/${id}`, { withCredentials: true });
              if (saved.data.saved) setMatchResults(prev => ({ ...prev, matches: saved.data.matches }));
            } catch { /* non-blocking */ }
          }
        } catch (e) {
          pollFailures += 1;
          if (pollFailures >= 8) {
            clearInterval(poll);
            setIntroSending(false);
            toast.error('Lost connection to progress endpoint. Refresh and click Resume to continue.', { duration: 9000 });
          }
        }
      }, 2000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : errMsg(err, 'Failed to start campaign'));
      setIntroSending(false);
    }
  };

  const handleResumeInterrupted = async () => {
    if (!campaignPlan?.interrupted_campaign_id) return;
    await handleSendIntroEmails({ resumeCampaignId: campaignPlan.interrupted_campaign_id });
  };

  const handleRetryFailed = async () => {
    if (!campaignId) return;
    setIntroSending(true);
    try {
      await axios.post(`${API}/outreach-campaigns/${campaignId}/retry-failed`, {}, { withCredentials: true });
      toast.success('Retrying failed rows…');
      // Reuse the same poll loop by calling handleSendIntroEmails would
      // create a new campaign — instead just re-poll the existing one.
      const poll = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/outreach-campaigns/${campaignId}`, { withCredentials: true });
          setCampaignProgress(s.data);
          if (s.data.status === 'completed' || s.data.status === 'interrupted' || s.data.status === 'failed') {
            clearInterval(poll);
            setIntroSending(false);
            toast.success(`Retry complete — sent ${s.data.counts.sent}, failed ${s.data.counts.failed}`);
          }
        } catch {
          clearInterval(poll);
          setIntroSending(false);
        }
      }, 2000);
    } catch (err) {
      toast.error(errMsg(err, 'Retry failed'));
      setIntroSending(false);
    }
  };

  const handleViewSentCampaigns = async () => {
    try {
      const r = await axios.get(`${API}/pharma-contacts/${id}/campaigns`, { withCredentials: true });
      setHistoricalCampaigns(r.data.campaigns || []);
      setViewSentOpen(true);
    } catch {
      toast.error('Failed to load campaign history');
    }
  };

  const fetchContact = async () => {
    try {
      const res = await axios.get(`${API}/pharma-contacts/${id}`, { withCredentials: true });
      setContact(res.data);
      // Aug 2026 · Company-level stage gate fallback — if this contact
      // has no per-person `preferred_stages`, we still want the UI to
      // show the parent pharma's org-wide gate (e.g. "Company gate:
      // Phase 3+ · inherited from Sanofi") so BD leads know the send
      // block will still fire. Fetched lazily and stored in state.
      try {
        const _pid = res.data.company_profile_id;
        if (_pid) {
          const pr = await axios.get(`${API}/company-profiles/${_pid}`, { withCredentials: true });
          setCompanyStageGate({
            company_name: pr.data.company_name || '',
            min_stage: pr.data?.stage_gate?.min_stage || '',
            note: pr.data?.stage_gate?.note || '',
          });
        } else {
          setCompanyStageGate({ company_name: '', min_stage: '', note: '' });
        }
      } catch { setCompanyStageGate({ company_name: '', min_stage: '', note: '' }); }
      setForm({
        name: res.data.name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        designation: res.data.designation || '',
        linkedin_url: res.data.linkedin_url || '',
        company_name: res.data.company_name || '',
        company_type: res.data.company_type || 'Big Pharma',
        location: res.data.location || '',
        department: res.data.department || '',
        interest_areas: res.data.interest_areas || [],
        disease_areas: res.data.disease_areas || {},
        excluded_therapeutic_areas: res.data.excluded_therapeutic_areas || [],
        preferred_stages: res.data.preferred_stages || [],
        preferred_modalities: res.data.preferred_modalities || [],
        // Aug 2026 · granular fields (InterestTagger + deck enrichment).
        therapeutic_areas_specific: res.data.therapeutic_areas_specific || [],
        scientific_preferences: res.data.scientific_preferences || [],
        focus_notes: res.data.focus_notes || [],
        excluded_modalities: res.data.excluded_modalities || [],
        excluded_indications: res.data.excluded_indications || [],
        priority_targets_moas: res.data.priority_targets_moas || [],
        notes: res.data.notes || '',
      });
    } catch {
      toast.error('Failed to load contact details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyNames = async () => {
    try {
      const res = await axios.get(`${API}/pharma-companies/names`, { withCredentials: true });
      setCompanyNames(res.data || []);
    } catch {}
  };

  useEffect(() => { fetchContact(); }, [id]);

  // Auto-open the appropriate email dialog when navigated from the
  // Alerts page. Two URL params handle both flows:
  //   ?openPitchPartnersForAlerts=asset_id_1,asset_id_2  → consolidated
  //     Pitch Active Partners email (single email TO the pharma contact)
  //   ?openIntroForAlerts=asset_id_1,asset_id_2          → biotech-direction
  //     intro outreach (one email per biotech CEO)
  useEffect(() => {
    if (!matchResults?.matches?.length) return;
    const params = new URLSearchParams(window.location.search);
    const pitchRaw = params.get('openPitchPartnersForAlerts');
    const introRaw = params.get('openIntroForAlerts');
    if (!pitchRaw && !introRaw) return;

    const wantedIds = new Set(
      (pitchRaw || introRaw).split(',').map(s => s.trim()).filter(Boolean)
    );
    if (!wantedIds.size) return;
    const targets = matchResults.matches.filter(m => wantedIds.has(m.asset_id));
    if (!targets.length) {
      toast.info('Alerted assets are not in this contact\'s saved matches yet — refresh matches first');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    if (pitchRaw) {
      // Synthesize matchResults snapshot scoped to alert-targets, then open
      // the Pitch Active Partners dialog. We can't just call
      // openActivePartnersPitch() directly because it filters
      // matchResults.matches by is_my_company — and matchResults still
      // contains everything. Instead we fan out to the dialog state directly.
      const partnerAssets = targets.map(m => ({
        company_id: m.company_id,
        company_name: m.company_name,
        asset_name: m.asset_name,
        asset_id: m.asset_id || '',
        stage: m.stage || '',
        indication: m.indication || '',
        modality: m.modality || '',
        target: m.target || '',
        platform: m.platform || '',
        description: m.description || '',
        molecule_description: m.molecule_description || '',
      }));
      // Reuse the existing setup that openActivePartnersPitch performs.
      openActivePartnersPitch(partnerAssets);
    } else {
      handleOpenIntroEmail();
      setIntroEmailTargets(targets);
    }
    window.history.replaceState({}, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchResults]);

  // Auto-load saved matches on page load
  useEffect(() => {
    if (!id) return;
    const loadSavedMatches = async () => {
      try {
        const res = await axios.get(`${API}/ai/saved-matches/${id}`, { withCredentials: true });
        if (res.data.saved) {
          setMatchResults({
            matches: res.data.matches,
            total_matches: res.data.total_matches,
            saved: true,
            updated_at: res.data.updated_at,
            company_signals: res.data.company_signals || null,
            // Restore prompt provenance so the last prompt used is visible
            // and re-runnable on revisit.
            ...(res.data.prompt ? {
              _source: 'prompt',
              _prompt_text: res.data.prompt,
              extracted: res.data.prompt_extracted || null,
            } : {}),
          });
          if (res.data.prompt) {
            setPromptText(res.data.prompt);
            if (typeof res.data.prompt_strict === 'boolean') setStrictMatch(res.data.prompt_strict);
          }
        }
      } catch {}
    };
    loadSavedMatches();
  }, [id]);

  const handleCompanyNameChange = (val) => {
    setForm({ ...form, company_name: val });
    if (val.trim().length >= 1) {
      const filtered = companyNames.filter(n => n.toLowerCase().includes(val.toLowerCase()));
      setCompanySuggestions(filtered.slice(0, 8));
      setShowCompanySuggestions(filtered.length > 0);
    } else {
      setShowCompanySuggestions(false);
    }
  };

  const selectCompanyName = (name) => {
    setForm({ ...form, company_name: name });
    setShowCompanySuggestions(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/pharma-contacts/${id}`, form, { withCredentials: true });
      setContact({ ...contact, ...res.data, outreach: contact.outreach });
      setEditing(false);
      const oldCompany = contact.company_name || '';
      const newCompany = form.company_name || '';
      if (oldCompany !== newCompany && newCompany) {
        toast.success(`Company changed to "${newCompany}". All linked records updated. ${companyNames.includes(newCompany) ? '' : 'New company profile created.'}`);
      } else {
        toast.success('Contact updated. All linked records have been updated.');
      }
      fetchContact();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${contact.name}? This will also remove all linked outreach records.\n\nThe contact identity is retained in the suppression registry so no import, AI mapping or enrichment can silently re-add them.`)) return;
    const optOut = window.confirm('Did this person explicitly request NOT to be contacted (opt-out)?\n\nOK = Yes, mark as DO NOT CONTACT (never auto-restored)\nCancel = No, just manually removed (restorable)');
    try {
      await axios.delete(`${API}/pharma-contacts/${id}?suppress_status=${optOut ? 'DO_NOT_CONTACT' : 'MANUALLY_REMOVED'}`, { withCredentials: true });
      navigate('/pharma');
      setTimeout(() => toast.success(optOut ? 'Contact removed and marked Do Not Contact' : 'Contact deleted — retained in suppression registry'), 300);
    } catch {
      toast.error('Failed to delete contact');
    }
  };

  // ── DNC dialog ─────────────────────────────────────────────────────────
  // Two independent actions, exposed in one dialog because users invariably
  // want both — block + redirect. Either can be applied alone.
  const openDncDialog = () => {
    setDncReason((contact?.do_not_contact?.reason) || '');
    setReassignTargetSearch('');
    setReassignTargetResults([]);
    setReassignTargetPicked(null);
    setDncDialogOpen(true);
  };

  const searchReassignTargets = async (q) => {
    setReassignTargetSearch(q);
    if (!q.trim() || q.trim().length < 2) {
      setReassignTargetResults([]);
      return;
    }
    try {
      const { data } = await axios.get(
        `${API}/pharma-contacts?search=${encodeURIComponent(q.trim())}&limit=10`,
        { withCredentials: true },
      );
      // Filter out the current contact — can't reassign to self.
      setReassignTargetResults((data.items || []).filter(c => c.id !== id));
    } catch {
      setReassignTargetResults([]);
    }
  };

  const saveDncFlag = async (flagged) => {
    setDncSaving(true);
    try {
      const payload = { flagged };
      if (flagged) {
        payload.reason = dncReason.trim();
        if (reassignTargetPicked?.id) payload.redirect_to_contact_id = reassignTargetPicked.id;
      }
      const { data } = await axios.patch(
        `${API}/pharma-contacts/${id}/dnc`,
        payload,
        { withCredentials: true },
      );
      setContact({ ...contact, do_not_contact: data.do_not_contact });
      toast.success(flagged ? 'Marked Do Not Contact' : 'Removed Do Not Contact flag');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update flag');
    } finally {
      setDncSaving(false);
    }
  };

  const runReassign = async () => {
    if (!reassignTargetPicked?.id) {
      toast.error('Pick a target contact first');
      return;
    }
    if (!window.confirm(`Move all of ${contact.name}'s outreach to ${reassignTargetPicked.name} (${reassignTargetPicked.email || 'no email'})? Existing evaluations & statuses are preserved.`)) {
      return;
    }
    setReassignRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/pharma-contacts/${id}/reassign-outreach`,
        { target_contact_id: reassignTargetPicked.id },
        { withCredentials: true },
      );
      toast.success(`Moved ${data.moved} evaluation${data.moved === 1 ? '' : 's'} to ${data.to_contact_name}`);
      // Refresh contact so the outreach section under this contact empties
      // out (since all rows now belong to the target).
      const { data: refreshed } = await axios.get(`${API}/pharma-contacts/${id}`, { withCredentials: true });
      setContact(refreshed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reassign failed');
    } finally {
      setReassignRunning(false);
    }
  };

  const addInterest = () => {
    const val = interestInput.trim();
    if (val && !form.interest_areas.includes(val)) {
      setForm({ ...form, interest_areas: [...form.interest_areas, val] });
      setInterestInput('');
    }
  };

  const removeInterest = (area) => {
    setForm({ ...form, interest_areas: form.interest_areas.filter(a => a !== area) });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">Contact not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/pharma')}>Back to list</Button>
      </div>
    );
  }

  const outreach = contact.outreach || [];

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="pharma-contact-detail-page">
      {/* Back Button */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/pharma')} className="text-slate-500 hover:text-slate-700" data-testid="back-to-pharma">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>

      {/* DNC banner — shown only when flagged */}
      {(contact.do_not_contact?.flagged) && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 mb-4 flex items-start justify-between gap-3" data-testid="dnc-banner">
          <div className="flex items-start gap-2 min-w-0">
            <ShieldOff className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <div className="min-w-0 text-xs">
              <p className="font-semibold text-red-800">DO NOT CONTACT — outreach to this contact is blocked.</p>
              {contact.do_not_contact?.reason && (
                <p className="text-red-700/80 mt-0.5">Reason: {contact.do_not_contact.reason}</p>
              )}
              {contact.do_not_contact?.redirect_to_contact_id && (
                <p className="text-red-700/80 mt-0.5">
                  Redirect to:{' '}
                  <Link
                    to={`/pharma/${contact.do_not_contact.redirect_to_contact_id}`}
                    className="font-semibold underline text-red-800"
                    data-testid="dnc-redirect-link"
                  >
                    open replacement contact
                  </Link>
                </p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="text-[11px] h-7 border-red-300 text-red-700 hover:bg-red-100"
            onClick={() => saveDncFlag(false)}
            disabled={dncSaving}
            data-testid="dnc-clear-btn"
          >
            Clear flag
          </Button>
        </div>
      )}

      {/* Header — name + designation in a clean horizontal block on top,
          action buttons in their own wrap-friendly row below so 6+ buttons
          don't squeeze the name column into one-word-per-line stacking. */}
      <div className="mb-8">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 mb-3">
          <h1
            className="text-3xl font-bold tracking-tight text-slate-900 whitespace-nowrap"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="contact-name-header"
          >
            {contact.name}
          </h1>
          {contact.designation && (
            <span className="text-sm text-slate-600">{contact.designation}</span>
          )}
          {contact.company_name && (
            contact.company_profile_id ? (
              <a
                href={`/company-profile/${contact.company_profile_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-sky-600 hover:underline transition-colors"
                data-testid="contact-company-link"
                title={`Open ${contact.company_name} profile in a new tab`}
              >
                <Building className="w-3.5 h-3.5" />{contact.company_name}
              </a>
            ) : (
              <span className="flex items-center gap-1 text-sm text-slate-500" data-testid="contact-company-name">
                <Building className="w-3.5 h-3.5" />{contact.company_name}
              </span>
            )
          )}
          {contact.company_type && (
            <Badge variant="outline" className="text-xs">{contact.company_type}</Badge>
          )}
          {contact.corporate_group && (
            <Badge
              variant="outline"
              className="text-xs bg-slate-50 text-slate-500 border-slate-300"
              title={`Direct company: ${contact.company_name || '—'} · Corporate group: ${contact.corporate_group}. Company affiliation and outreach scope are separate concepts.`}
              data-testid="contact-corporate-group-badge"
            >
              Member of {contact.corporate_group}
            </Badge>
          )}
          {contact.partnering_scope && (
            <Badge
              variant="outline"
              className="text-xs bg-violet-50 text-violet-700 border-violet-300"
              title="Partnering scope — what opportunities this person handles (may span the corporate group), independent of their direct company affiliation."
              data-testid="contact-partnering-scope-badge"
            >
              Scope: {contact.partnering_scope}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={handleMatchAssets}
            disabled={matchLoading}
            className="bg-sky-600 text-white hover:bg-sky-700"
            data-testid="match-assets-btn"
          >
            {matchLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-2" />}
            {matchLoading ? 'Matching...' : matchResults?.saved ? 'Refresh Matches' : 'Match Assets'}
          </Button>
          <Button
            onClick={() => promptOpen ? setPromptOpen(false) : openPromptPanel()}
            variant="outline"
            className="border-violet-200 text-violet-700 hover:bg-violet-50"
            data-testid="match-by-prompt-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Match by Prompt
          </Button>
          <Button
            onClick={handleShareMatchLink}
            disabled={shareLoading}
            variant="outline"
            className="border-sky-200 text-sky-700 hover:bg-sky-50"
            data-testid="share-match-link-btn"
          >
            {shareLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
            Share Link
          </Button>
          <Button
            onClick={() => { setEditing(true); fetchCompanyNames(); }}
            className="bg-violet-600 text-white hover:bg-violet-700"
            data-testid="edit-contact-btn"
          >
            <Pencil className="w-4 h-4 mr-2" /> Edit Contact
          </Button>
          <Button
            onClick={openDncDialog}
            variant="outline"
            className={`${contact.do_not_contact?.flagged ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            data-testid="manage-dnc-btn"
            title={contact.do_not_contact?.flagged ? 'Currently blocked — manage flag / redirect' : 'Block this contact or reassign their evaluations to someone else'}
          >
            <ShieldOff className="w-4 h-4 mr-2" />
            {contact.do_not_contact?.flagged ? 'Blocked' : 'Block / Reassign'}
          </Button>
          <Button
            onClick={handleDelete}
            variant="outline"
            className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
            data-testid="delete-contact-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        </div>
      </div>

      {/* Spawned Accounts — at-a-glance list of every licensing account
          already created from this contact, with a one-click "+ New Account"
          for the case when one contact covers multiple TA franchises
          (e.g. Eric Yeung covers BOTH Oncology AND Rare Disease at Takeda). */}
      <Card className="border border-slate-200 shadow-sm mb-6" data-testid="spawned-accounts-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FolderPlus className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Spawned Accounts
              </h3>
              <Badge variant="secondary" className="text-xs">{spawnedAccounts.length}</Badge>
            </div>
            <Button
              size="sm"
              onClick={openCreateAccountDialog}
              className="h-7 text-xs bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="spawn-new-account-btn"
            >
              <FolderPlus className="w-3.5 h-3.5 mr-1.5" /> + New Account
            </Button>
          </div>
          {spawnedAccounts.length > 0 ? (
            <div className="space-y-1.5">
              {spawnedAccounts.map(a => (
                <div
                  key={a.project_id}
                  onClick={() => navigate(`/accounts/${a.project_id}`)}
                  className="flex items-center gap-3 px-3 py-2 rounded border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 cursor-pointer transition group"
                  data-testid={`spawned-account-row-${a.project_id}`}
                >
                  <Badge
                    className={`text-[10px] px-1.5 py-0.5 shrink-0 ${
                      a.status === 'Active' ? 'bg-emerald-100 text-emerald-700'
                      : a.status === 'On Hold' ? 'bg-amber-100 text-amber-700'
                      : a.status === 'Closed' ? 'bg-slate-100 text-slate-500'
                      : 'bg-sky-100 text-sky-700'
                    }`}
                  >
                    {a.status}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate group-hover:text-emerald-700">{a.project_name}</p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                      {(a.therapeutic_areas || []).slice(0, 3).map(t => (
                        <span key={t} className="text-[10px] text-slate-500 bg-sky-50 border border-sky-100 rounded px-1.5 py-0.5">{t}</span>
                      ))}
                      {a.therapeutic_areas?.length > 3 && (
                        <span className="text-[10px] text-slate-400">+{a.therapeutic_areas.length - 3}</span>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-emerald-500 shrink-0" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-3 text-xs text-slate-400">
              No accounts spawned yet — click "+ New Account" to open one from this contact's stated priorities.
            </div>
          )}
        </CardContent>
      </Card>


      {/* Routing-policy warning — shown when the pharma has requested that all
          BD outreach be routed through a specific single point-of-contact and
          this contact is NOT that POC. */}
      {contact.routing_policy?.mode === 'single_poc' && !contact.is_poc && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="routing-policy-warning">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                Do not email this contact directly — {contact.company_name} has requested single-point-of-contact BD routing.
              </p>
              <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                Route all partnering outreach through{' '}
                <span className="font-semibold">{contact.routing_policy.poc_name || contact.routing_policy.poc_email}</span>
                {contact.routing_policy.poc_email && (
                  <> — <a href={`mailto:${contact.routing_policy.poc_email}`} className="underline decoration-amber-400 hover:text-amber-900">{contact.routing_policy.poc_email}</a></>
                )}
                .
                {contact.routing_policy.note && (
                  <span className="block mt-1 text-amber-700 italic">“{contact.routing_policy.note}”</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
      {contact.routing_policy?.mode === 'single_poc' && contact.is_poc && (
        <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="routing-policy-poc-badge">
          <p className="text-xs text-emerald-800">
            <span className="font-semibold">Single point of contact</span> — {contact.company_name} routes all BD partnering outreach through this person.
          </p>
        </div>
      )}

      {/* Aug 2026 · Partnering Mandates — structured person-level intelligence
          extracted from corporate decks (Person ↔ Section ↔ Mandate). */}
      {(contact.partnering_mandates || []).length > 0 && (
        <Card className="border border-violet-200 shadow-sm mb-8" data-testid="partnering-mandates-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Partnering Mandates</h3>
              <Badge variant="secondary" className="text-[10px]">{contact.partnering_mandates.length}</Badge>
              <span className="text-[11px] text-slate-400">from partnering decks · feeds asset matching &amp; routing</span>
            </div>
            <div className={`grid grid-cols-1 ${contact.partnering_mandates.length > 1 ? 'lg:grid-cols-2' : ''} gap-3`}>
              {contact.partnering_mandates.map((m, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3 bg-violet-50/30" data-testid={`mandate-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-800">{m.section || m.therapeutic_area}</span>
                    <Badge
                      variant="outline"
                      className={`text-[9px] ${m.evidence_level === 'explicit_individual' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-sky-50 text-sky-700 border-sky-300'}`}
                      title={m.evidence_level === 'explicit_individual' ? 'Document explicitly assigns this preference to this individual' : "Document lists this person as a contact for this team and describes the team's mandate"}
                    >
                      {m.evidence_level === 'explicit_individual' ? 'Explicit Individual' : 'Explicit Team'}
                    </Badge>
                  </div>
                  {(m.diseases || []).length > 0 && (
                    <div className="mt-2"><span className="text-[10px] font-semibold text-slate-500 uppercase">Diseases</span>
                      <div className="flex flex-wrap gap-1 mt-0.5">{m.diseases.map((d, j) => <Badge key={j} variant="secondary" className="text-[10px]">{d}</Badge>)}</div></div>
                  )}
                  {(m.interests || []).length > 0 && (
                    <div className="mt-2"><span className="text-[10px] font-semibold text-slate-500 uppercase">Interests</span>
                      <div className="flex flex-wrap gap-1 mt-0.5">{m.interests.map((d, j) => <Badge key={j} className="text-[10px] bg-sky-50 text-sky-700 border border-sky-200">{d}</Badge>)}</div></div>
                  )}
                  {(m.technologies || []).length > 0 && (
                    <div className="mt-2"><span className="text-[10px] font-semibold text-slate-500 uppercase">Technologies</span>
                      <div className="flex flex-wrap gap-1 mt-0.5">{m.technologies.map((d, j) => <Badge key={j} className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200">{d}</Badge>)}</div></div>
                  )}
                  <div className="flex flex-wrap gap-3 mt-2">
                    {(m.stages || []).length > 0 && (
                      <span className="text-[11px] text-slate-600"><span className="font-semibold">Stage:</span> {m.stages.join(', ')}</span>
                    )}
                    {(m.modalities || []).length > 0 && (
                      <span className="text-[11px] text-slate-600"><span className="font-semibold">Modality:</span> {m.modalities.join(', ')}</span>
                    )}
                  </div>
                  {(m.exclusions || []).length > 0 && (
                    <div className="mt-2"><span className="text-[10px] font-semibold text-rose-600 uppercase">Excluded</span>
                      <div className="flex flex-wrap gap-1 mt-0.5">{m.exclusions.map((d, j) => <Badge key={j} className="text-[10px] bg-rose-50 text-rose-700 border border-rose-200" data-testid={`mandate-${i}-exclusion-${j}`}>{d}</Badge>)}</div></div>
                  )}
                  <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-400" title={`Evidence: ${m.evidence_level} · Confidence: ${m.confidence || 'High'}`}>
                    Source: {m.source_document}{m.page ? ` · p.${m.page}` : ''}{m.extracted_at ? ` · ${String(m.extracted_at).slice(0, 10)}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact Info + Interests */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="border border-slate-200 shadow-sm" data-testid="contact-info-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-4 h-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Contact Information</h3>
            </div>
            <div className="space-y-3">
              {contact.email && (
                <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                  <Mail className="w-4 h-4 text-sky-500 shrink-0" />
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Email</p>
                    <a href={`mailto:${contact.email}`} className="text-sm text-sky-600 hover:underline">{contact.email}</a>
                    <CopyEmail email={contact.email} />
                  </div>
                </div>
              )}
              {contact.phone && (
                <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                  <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Phone</p>
                    <p className="text-sm text-slate-700">{contact.phone}</p>
                  </div>
                </div>
              )}
              {contact.location && (
                <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                  <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Country</p>
                    <p className="text-sm text-slate-700">{contact.location}</p>
                  </div>
                </div>
              )}
              {contact.department && (
                <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                  <Briefcase className="w-4 h-4 text-slate-400 shrink-0" />
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase">Department</p>
                    <p className="text-sm text-slate-700">{contact.department}</p>
                  </div>
                </div>
              )}
              {contact.linkedin_url && (
                <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3" data-testid="contact-linkedin-block">
                  <Linkedin className="w-4 h-4 text-[#0A66C2] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-slate-400 uppercase">LinkedIn</p>
                    <a
                      href={contact.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-sky-600 hover:underline truncate block"
                      data-testid="contact-linkedin-link"
                    >
                      {contact.linkedin_url.replace(/^https?:\/\/(www\.)?/, '')}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm" data-testid="interest-areas-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase className="w-4 h-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Therapeutic Areas</h3>
              <Badge variant="secondary" className="text-[10px]">{(contact.interest_areas || []).length}</Badge>
              <button
                type="button"
                onClick={() => { setEditing(true); fetchCompanyNames(); }}
                className="ml-auto p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-violet-600 transition-colors"
                title="Edit Therapeutic Areas"
                data-testid="edit-therapeutic-areas-inline"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
            {/* Feb 2026 · AI tagger. Type free-text interests
                (e.g. "gastrointestinal health and immune system") →
                Claude structures them into interest_areas / modalities /
                stages / geographies / excluded / focus_notes → user
                approves the chips → applied to the profile. */}
            <div className="mb-4">
              <InterestTagger
                entityType="pharma_contact"
                entityId={contact.id}
                initialText={contact.interest_notes || ''}
                onApplied={fetchContact}
              />
            </div>
            {(contact.interest_areas || []).length > 0 ? (
              <div className="space-y-2.5">
                {contact.interest_areas.map((area, i) => {
                  // Each TA gets its own row with disease-area sub-pills
                  // beneath. Disease areas come from
                  //   contact.disease_areas[area]
                  // (TA→list map). Empty list = "interested in the whole TA".
                  const sub = (contact.disease_areas || {})[area] || [];
                  return (
                    <div key={i} className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs px-3 py-1">{area}</Badge>
                        {sub.length === 0 && (
                          <span className="text-[10px] text-slate-400 italic">All disease areas</span>
                        )}
                      </div>
                      {sub.length > 0 && (
                        <div className="flex flex-wrap gap-1 ml-2">
                          {sub.map((d, j) => (
                            <Badge key={j} variant="outline" className="text-[10px] px-2 py-0.5 bg-white text-slate-600 border-slate-300">
                              {d}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-400">No therapeutic areas specified</p>
            )}
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm" data-testid="excluded-areas-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <X className="w-4 h-4 text-rose-400" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Excluded Areas</h3>
              <Badge variant="secondary" className="text-[10px]">{(contact.excluded_therapeutic_areas || []).length}</Badge>
              <button
                type="button"
                onClick={() => { setEditing(true); fetchCompanyNames(); }}
                className="ml-auto p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-violet-600 transition-colors"
                title="Edit Excluded Areas"
                data-testid="edit-excluded-areas-inline"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
            {(contact.excluded_therapeutic_areas || []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {contact.excluded_therapeutic_areas.map((area, i) => (
                  <Badge key={i} className="text-xs px-3 py-1 bg-rose-50 text-rose-600 border border-rose-200">{area}</Badge>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400">No exclusions set</p>
            )}
          </CardContent>
        </Card>

        <StructuredInterestCards
          entity={contact}
          onEdit={() => { setEditing(true); fetchCompanyNames(); }}
        />

        <Card className="border border-slate-200 shadow-sm" data-testid="preferred-stages-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <FlaskConical className="w-4 h-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Preferred Stages</h3>
              <Badge variant="secondary" className="text-[10px]">{(contact.preferred_stages || []).length}</Badge>
              <button
                type="button"
                onClick={() => { setEditing(true); fetchCompanyNames(); }}
                className="ml-auto p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-violet-600 transition-colors"
                title="Edit Preferred Stages"
                data-testid="edit-preferred-stages-inline"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
            {(contact.preferred_stages || []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {contact.preferred_stages.map((stage, i) => (
                  <Badge key={i} className="text-xs px-3 py-1 bg-sky-100 text-sky-700 border border-sky-200">{stage}</Badge>
                ))}
              </div>
            ) : companyStageGate.min_stage ? (
              // Aug 2026 · No per-person preferred_stages → the parent
              // pharma's org-wide `stage_gate.min_stage` is enforced at
              // send time. Surface it so BD leads see the inheritance
              // and know the send block will still fire.
              <div className="flex flex-col gap-2" data-testid="inherited-company-stage-gate">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="text-xs px-3 py-1 bg-indigo-100 text-indigo-700 border border-indigo-200">
                    Company gate · {companyStageGate.min_stage}+
                  </Badge>
                  <span className="text-[10px] text-slate-500">
                    inherited from <span className="font-semibold">{companyStageGate.company_name}</span>
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed">
                  This contact has no per-person preferred stages. Outreach to them is blocked for assets earlier than <strong>{companyStageGate.min_stage}</strong>, matching the org-wide stage gate on the Company Profile. Set explicit preferred stages below to override for this person only.
                </p>
              </div>
            ) : (
              <p className="text-xs text-slate-400">No preferred stages specified</p>
            )}
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm" data-testid="preferred-modalities-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <FlaskConical className="w-4 h-4 text-violet-400" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Preferred Modalities</h3>
              <Badge variant="secondary" className="text-[10px]">{(contact.preferred_modalities || []).length}</Badge>
              <button
                type="button"
                onClick={() => { setEditing(true); fetchCompanyNames(); }}
                className="ml-auto p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-violet-600 transition-colors"
                title="Edit Preferred Modalities"
                data-testid="edit-preferred-modalities-inline"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
            {(contact.preferred_modalities || []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {contact.preferred_modalities.map((mod, i) => (
                  <Badge key={i} className="text-xs px-3 py-1 bg-violet-100 text-violet-700 border border-violet-200">{mod}</Badge>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400">No preferred modalities specified</p>
            )}
          </CardContent>
        </Card>

        {/* Corporate Presentation - shows both contact-embedded (legacy)
            AND company-inherited (Feb 2026 single-source-of-truth) decks.
            Contact-embedded entries appear first, then the inherited
            block visually separated with a "Shared account intel" label
            so the user knows those decks live on the company profile. */}
        {((contact.corporate_decks || []).length > 0 || (contact.inherited_decks || []).length > 0) && (
          <Card className="border border-slate-200 shadow-sm lg:col-span-2" data-testid="corporate-deck-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-violet-500" />
                  <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Corporate Presentation</h3>
                  <Badge variant="secondary" className="text-[10px]">
                    {(contact.corporate_decks || []).length + (contact.inherited_decks || []).length}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer" title="Upload a deck for this account (stored once at the company level)">
                    <input type="file" accept=".pdf,.ppt,.pptx" className="hidden" onChange={handleDeckUpload} disabled={deckUploading} data-testid="deck-upload-input" />
                    <Button variant="outline" size="sm" className="gap-1.5 text-xs pointer-events-none" disabled={deckUploading} data-testid="deck-upload-btn" asChild>
                      <span>
                        {deckUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {deckUploading ? 'Uploading...' : 'Upload More'}
                      </span>
                    </Button>
                  </label>
                  <PharmaEnrichButton
                    entityType="pharma_contact"
                    entityId={contact.id}
                    hasDeck={(contact.corporate_decks || []).length > 0 || (contact.inherited_decks || []).length > 0}
                    onDone={fetchContact}
                  />
                </div>
              </div>
              {(contact.inherited_decks || []).length > 0 && (
                <div className="mb-3 text-[10px] uppercase tracking-wider text-violet-600/80 font-semibold flex items-center gap-1.5" data-testid="inherited-decks-label">
                  <FileText className="w-3 h-3" />
                  Inherited from {contact.company_name || 'company'} · {contact.inherited_decks.length} shared deck(s)
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(contact.inherited_decks || []).map((deck, i) => (
                  <div key={`inh-${i}`} className="flex items-center gap-3 bg-violet-50/40 rounded-lg p-3 group border border-violet-100" data-testid={`inherited-deck-item-${i}`}>
                    <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                      <FileText className="w-4 h-4 text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{deck.filename}</p>
                      <p className="text-[10px] text-slate-400">
                        {deck.size ? `${(deck.size / 1024 / 1024).toFixed(1)} MB · ` : ''}{deck.uploaded_at ? new Date(deck.uploaded_at).toLocaleDateString() : ''}
                      </p>
                      <p className="text-[10px] text-violet-500 truncate">
                        Company-level · {(deck.shared_by || []).length > 0
                          ? `shared by ${(deck.shared_by || []).map(s => s.contact_name).filter(Boolean).join(', ') || 'account'}`
                          : 'account intel'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/company-profiles/${deck.inherited_from_profile_id}/download-deck/${encodeURIComponent(deck.filename)}`, { responseType: 'blob', withCredentials: true });
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = deck.filename;
                          link.click();
                          window.URL.revokeObjectURL(url);
                        } catch { toast.error('Failed to download file'); }
                      }} className="text-xs font-medium text-sky-600 hover:text-sky-700 hover:underline px-2 py-1 rounded hover:bg-sky-50 transition-colors cursor-pointer" data-testid={`inherited-deck-download-${i}`}>
                        Download
                      </button>
                    </div>
                  </div>
                ))}
                {(contact.corporate_decks || []).map((deck, i) => (
                  <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3 group" data-testid={`deck-item-${i}`}>
                    <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                      <FileText className="w-4 h-4 text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{deck.filename}</p>
                      <p className="text-[10px] text-slate-400">
                        {(deck.size / 1024 / 1024).toFixed(1)} MB &middot; {new Date(deck.uploaded_at).toLocaleDateString()}
                      </p>
                      {deck.shared_by_contact_name && (
                        <p className="text-[10px] text-violet-500 truncate">Shared by {deck.shared_by_contact_name}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/pharma-contacts/${contact.id}/download-deck/${encodeURIComponent(deck.filename)}`, { responseType: 'blob', withCredentials: true });
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = deck.filename;
                          link.click();
                          window.URL.revokeObjectURL(url);
                        } catch { toast.error('Failed to download file'); }
                      }} className="text-xs text-sky-600 hover:text-sky-700 hover:underline px-2 py-1 rounded hover:bg-sky-50 cursor-pointer" data-testid={`deck-view-${i}`}>Download</button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => handleDeleteDeck(deck.filename)} data-testid={`deck-delete-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {viewEmailRecord && <SentEmailDialog record={viewEmailRecord} onClose={() => setViewEmailRecord(null)} />}

      {/* Outreach / Deals Involvement */}
      <Card className="border border-slate-200 shadow-sm" data-testid="contact-outreach-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-4 h-4 text-sky-500" />
            <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Outreach & Deal Involvement</h3>
            <Badge variant="secondary" className="text-xs">{outreach.length}</Badge>
          </div>

          {outreach.length > 0 ? (
            <div className="border border-slate-100 rounded-lg overflow-hidden">
              <div className="grid grid-cols-[1.5fr_1fr_1.5fr_0.8fr_0.7fr_76px] px-4 py-2 bg-slate-50 border-b border-slate-100">
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Biotech Company</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Asset</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Status</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Type</span>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Date</span>
                <span></span>
              </div>
              <div className="divide-y divide-slate-50 max-h-[400px] overflow-y-auto">
                {outreach.map((o) => (
                  <div key={o.id} className="grid grid-cols-[1.5fr_1fr_1.5fr_0.8fr_0.7fr_76px] px-4 py-3 items-center gap-2 hover:bg-slate-50/50" data-testid={`outreach-row-${o.id}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{o.biotech_company_name || '—'}</p>
                      {o.notes && <p className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[250px]">{o.notes}</p>}
                      {(o.match_basis || (o.match_asset_names && o.match_asset_names.length)) && (() => {
                        const isPrompt = o.match_basis === 'prompt';
                        const promptText = (o.match_prompt || (o.match_prompt_keywords || []).join(', ')).trim();
                        const ttLines = [
                          isPrompt
                            ? `Prompt-driven match · "${promptText || '—'}"`
                            : `Profile-driven match · TAs: ${(o.match_areas_used || []).join(', ') || '—'} · Modalities: ${(o.match_modalities_used || []).join(', ') || '—'} · Stages: ${(o.match_stages_used || []).join(', ') || '—'}`,
                          (o.match_asset_names && o.match_asset_names.length) ? `Triggered by: ${o.match_asset_names.join(' / ')}` : '',
                        ].filter(Boolean).join('\n');
                        return (
                          <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`match-context-${o.id}`}>
                            <span
                              title={ttLines}
                              className={`inline-flex items-center gap-1 px-1.5 py-0 rounded text-[10px] border ${
                                isPrompt
                                  ? 'bg-violet-50 text-violet-700 border-violet-200'
                                  : 'bg-slate-50 text-slate-600 border-slate-200'
                              }`}
                            >
                              {isPrompt ? 'Prompt' : 'Profile'}
                              {isPrompt && promptText && (
                                <span className="font-mono text-[9px] truncate max-w-[160px]">· {promptText}</span>
                              )}
                            </span>
                            {(o.match_asset_names || []).slice(0, 3).map((nm, idx) => (
                              <span key={idx} className="text-[10px] text-slate-500 italic truncate max-w-[140px]" title={`Triggered by ${nm}`}>
                                · {nm}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    {/* Aug 2026 · Asset involved in this outreach (user request) */}
                    <div data-testid={`outreach-asset-${o.id}`}>
                      {(o.asset_name || '').trim() ? (
                        <Badge variant="secondary" className="text-[10px] bg-violet-50 text-violet-700 whitespace-normal">{o.asset_name}</Badge>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </div>
                    <div>
                      <Badge className={`text-[10px] w-fit ${OUTREACH_COLORS[o.status] || 'bg-slate-100 text-slate-600'}`}>{o.status}</Badge>
                      {o.status_notes && (
                        <p className={`text-[10px] italic mt-1 leading-tight ${o.status === 'Declined' ? 'text-rose-500' : o.status === 'Ghosted' ? 'text-stone-500' : o.status === 'Awaiting Trial Data' ? 'text-orange-500' : 'text-slate-500'}`}>{o.status_notes}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-[10px] w-fit">{o.contact_type || 'Pharma'}</Badge>
                    <p className="text-[11px] text-slate-500 whitespace-nowrap" data-testid={`outreach-date-${o.id}`} title={o.created_at ? new Date(o.created_at).toLocaleString() : ''}>
                      {o.created_at ? new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </p>
                    <span className="flex items-center gap-1.5 justify-end">
                      {(o.email_body || o.email_subject) && (
                        <button onClick={() => setViewEmailRecord(o)} title="View sent email copy" className="text-slate-400 hover:text-sky-600" data-testid={`outreach-view-email-${o.id}`}>
                          <Mail className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <a href={`/biotech/${o.biotech_company_id}`} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-700 text-xs hover:underline">View</a>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Send className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No outreach entries linked to this contact</p>
              <p className="text-xs text-slate-400 mt-1">Outreach entries appear here when this contact is tagged in a biotech company's outreach tracker</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Sending Status Banner — visible on main page outside dialog */}
      {(() => {
        // Unified progress adapter — the new campaign engine emits
        // { counts:{sent,failed,remaining,planned,percent_complete}, status }
        // whereas the legacy in-memory task emitted { sent, failed, total,
        // status }. Map campaignProgress into the legacy shape so the
        // existing banner keeps rendering without a full rewrite.
        const derived = campaignProgress
          ? {
              sent: campaignProgress.counts?.sent || 0,
              failed: campaignProgress.counts?.failed || 0,
              skipped: campaignProgress.counts?.skipped || 0,
              total: campaignProgress.counts?.planned || 0,
              remaining: campaignProgress.counts?.remaining || 0,
              percent: campaignProgress.counts?.percent_complete || 0,
              status: campaignProgress.status,
              campaign: true,
            }
          : introProgress;
        const showBanner = introSending || (derived && (derived.status === 'completed' || derived.status === 'interrupted'));
        if (!showBanner) return null;
        const isDone = derived?.status === 'completed';
        const isInterrupted = derived?.status === 'interrupted';
        return (
        <div
          className={`mt-6 rounded-xl p-4 border-2 shadow-sm transition-all ${
            isDone ? 'bg-emerald-50 border-emerald-300'
            : isInterrupted ? 'bg-amber-50 border-amber-300'
            : 'bg-sky-50 border-sky-300 animate-pulse'
          }`}
          data-testid="email-status-banner"
        >
          <div className="flex items-center gap-3">
            {isDone ? (
              <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>
              </div>
            ) : isInterrupted ? (
              <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center shrink-0 text-white text-xs font-bold">
                ⏸
              </div>
            ) : (
              <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center shrink-0">
                <Loader2 className="w-4 h-4 animate-spin text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">
                {isDone
                  ? `Campaign complete — sent ${derived.sent}${derived.failed ? `, failed ${derived.failed}` : ''}${derived.skipped ? `, skipped ${derived.skipped} duplicate${derived.skipped === 1 ? '' : 's'}` : ''}`
                  : isInterrupted
                    ? `Campaign interrupted — ${derived.remaining} still to send. Click Resume Campaign to continue.`
                    : `Sending introduction emails…`}
              </p>
              {derived && (
                <div className="flex items-center gap-3 mt-1.5">
                  <div className="flex-1 bg-white/60 rounded-full h-2 border border-slate-200">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${
                        isDone ? 'bg-emerald-500' : isInterrupted ? 'bg-amber-500' : 'bg-sky-500'
                      }`}
                      style={{ width: `${derived.total > 0 ? ((derived.sent + derived.failed) / derived.total) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-slate-600 whitespace-nowrap" data-testid="campaign-progress-counter">
                    {derived.sent}{derived.skipped ? `+${derived.skipped}` : ''}/{derived.total}
                  </span>
                  {derived.failed > 0 && <span className="text-xs text-rose-600 font-medium">{derived.failed} failed</span>}
                </div>
              )}
            </div>
            {!introSending && (isDone || isInterrupted) && (
              <>
                {isInterrupted && (
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white shrink-0" onClick={() => handleSendIntroEmails({ resumeCampaignId: campaignId })} data-testid="banner-resume-btn">
                    Resume
                  </Button>
                )}
                {isDone && derived.failed > 0 && (
                  <Button size="sm" variant="outline" className="shrink-0" onClick={handleRetryFailed} data-testid="banner-retry-failed-btn">
                    Retry {derived.failed} failed
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="text-xs shrink-0" onClick={() => { setIntroProgress(null); setCampaignProgress(null); setIntroExcludedPartners([]); }}>Dismiss</Button>
              </>
            )}
          </div>
          {introExcludedPartners.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-200/60 text-xs">
              <p className="text-slate-600 font-medium flex items-center gap-1.5">
                <span>⭐</span> Skipped {introExcludedPartners.length} active partner{introExcludedPartners.length !== 1 ? 's' : ''} (flagged as "My Companies"):
              </p>
              <p className="text-slate-500 mt-1 leading-relaxed">{introExcludedPartners.join(', ')}</p>
            </div>
          )}
          {introExcludedDnc.length > 0 && (
            <div className="mt-3 pt-3 border-t border-rose-200/60 text-xs">
              <p className="text-rose-700 font-medium flex items-center gap-1.5">
                <span>⛔</span> Blocked {introExcludedDnc.length} "Do Not Contact" biotech{introExcludedDnc.length !== 1 ? 's' : ''}:
              </p>
              <ul className="mt-1 space-y-0.5 text-rose-600">
                {introExcludedDnc.map((x, i) => (
                  <li key={i}><span className="font-medium">{x.company_name}</span>{x.reason && <span className="italic text-rose-500"> — “{x.reason}”</span>}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        );
      })()}

      {/* Matched Assets */}
      {matchLoading && (
        <Card className="border border-slate-200 shadow-sm mt-6" data-testid="match-loading">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-sky-500 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Matching assets for {contact.name}...</p>
          </CardContent>
        </Card>
      )}

      {promptOpen && (
        <Card className="mt-6 border border-violet-200 bg-violet-50/30" data-testid="match-by-prompt-panel">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-violet-600" />
                  Match Assets by Custom Prompt
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Paste the pharma's query or interest statement. Claude extracts keywords and searches every asset in the database — indication, therapeutic area, modality, target, platform, mechanism.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPromptOpen(false)}
                data-testid="close-prompt-panel-btn"
                className="h-7 px-2"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
            <textarea
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder={'e.g., "Our business unit is focused on assets that address visual correction and vision restoration with clearly differentiated technology that aims to address an unmet need or advance the standard of care."'}
              className="w-full min-h-[120px] text-xs p-3 rounded-md border border-slate-300 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400 resize-y bg-white"
              maxLength={2000}
              data-testid="prompt-textarea"
            />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="text-[10px] text-slate-400">{promptText.length} / 2000</span>
              <div className="flex items-center gap-3">
                {/* Strict criteria toggle — OFF by default (recall-first). */}
                <button
                  type="button"
                  onClick={() => setStrictMatch((v) => !v)}
                  className="flex items-center gap-1.5 text-[11px] text-slate-600 hover:text-slate-900 transition-colors"
                  data-testid="strict-match-toggle"
                  title="Strict: modality becomes a hard filter (assets with no stated modality are dropped), and exclusions also apply to target + modality. Off = recall-first (may surface adjacent assets)."
                >
                  <span className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${strictMatch ? 'bg-violet-600' : 'bg-slate-300'}`}>
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${strictMatch ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                  </span>
                  <span className={strictMatch ? 'font-semibold text-violet-700' : ''}>Strict criteria</span>
                </button>
                <Button
                  onClick={handleMatchByPrompt}
                  disabled={promptLoading || !promptText.trim()}
                  className="bg-violet-600 hover:bg-violet-700 text-white text-xs gap-1.5"
                  size="sm"
                  data-testid="run-prompt-match-btn"
                >
                  {promptLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />}
                  {promptLoading ? 'Searching…' : 'Find Matching Assets'}
                </Button>
              </div>
            </div>
            {matchResults?._source === 'prompt' && matchResults.extracted && !promptLoading && (
              <div className="bg-white rounded-md border border-violet-100 p-3 text-[10px] text-slate-600 space-y-1">
                <p className="font-semibold text-slate-700 flex items-center gap-1.5">
                  Extracted concepts (used for search):
                  {matchResults.strict && (
                    <span className="inline-flex items-center rounded-full bg-violet-100 text-violet-700 px-1.5 py-0.5 text-[9px] font-medium" data-testid="strict-badge">
                      Strict criteria applied
                    </span>
                  )}
                </p>
                {(matchResults.extracted.therapeutic_areas || []).length > 0 && (
                  <p><span className="text-slate-400">Therapeutic areas: </span>{matchResults.extracted.therapeutic_areas.join(', ')}</p>
                )}
                {(matchResults.extracted.modalities || []).length > 0 && (
                  <p><span className="text-slate-400">Modalities: </span>{matchResults.extracted.modalities.join(', ')}{matchResults.strict && <span className="italic text-violet-600"> (hard filter · blank modality excluded)</span>}</p>
                )}
                {(matchResults.extracted.keywords || []).length > 0 && (
                  <p><span className="text-slate-400">Keywords: </span>{matchResults.extracted.keywords.slice(0, 20).join(', ')}</p>
                )}
                {(matchResults.extracted.exclude || []).length > 0 && (
                  <p data-testid="excluded-concepts"><span className="text-rose-400">Excluded: </span><span className="text-rose-600">{matchResults.extracted.exclude.join(', ')}</span>{matchResults.strict && <span className="italic text-rose-400"> (across indication · target · modality)</span>}</p>
                )}
                {(matchResults.extracted.constraints || []).length > 0 && (
                  <div className="mt-1 pt-1 border-t border-violet-100" data-testid="constraints-block">
                    <p className="font-medium text-violet-700 mb-0.5">TA-scoped modality constraints:</p>
                    {matchResults.extracted.constraints.map((b, ci) => (
                      <p key={ci} className="ml-2">
                        <span className="text-slate-500">{(b.therapeutic_areas || []).join(' / ')}:</span>{' '}
                        {(b.modalities || []).length > 0
                          ? <span className="font-medium">{b.modalities.join(', ')}</span>
                          : <span className="italic text-slate-400">no modality restriction</span>}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
            {detectedPrefs && !promptLoading && (() => {
              const rows = [
                { label: 'Therapeutic areas → Interest areas', vals: detectedPrefs.interest_areas, field: 'interest_areas' },
                { label: 'Preferred stages', vals: detectedPrefs.preferred_stages, field: 'preferred_stages' },
                { label: 'Preferred modalities', vals: detectedPrefs.preferred_modalities, field: 'preferred_modalities' },
                { label: 'Excluded areas', vals: detectedPrefs.excluded_therapeutic_areas, field: 'excluded_therapeutic_areas', danger: true },
              ].filter(r => (r.vals || []).length > 0);
              if (rows.length === 0) return null;
              // Which detected values are NEW (not already on the profile)?
              const isNew = (field, v) => !((form[field] || []).some(e => String(e).trim().toLowerCase() === String(v).trim().toLowerCase()));
              const newCount = rows.reduce((n, r) => n + r.vals.filter(v => isNew(r.field, v)).length, 0);
              return (
                <div className="mt-2 rounded-lg border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-sky-50 p-3" data-testid="detected-preferences-card">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-800">
                      Detected preferences from this prompt
                    </p>
                    {prefsApplied ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700" data-testid="prefs-applied-badge">
                        <CheckCircle className="w-3.5 h-3.5" /> Applied
                      </span>
                    ) : (
                      <Button onClick={applyPrefsToProfile} disabled={applyingPrefs || newCount === 0} size="sm"
                        className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]" data-testid="apply-prefs-btn">
                        {applyingPrefs ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                        {newCount === 0 ? 'Already on profile' : `Apply to profile${newCount ? ` (+${newCount} new)` : ''}`}
                      </Button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {rows.map((r) => (
                      <div key={r.field} className="flex flex-wrap items-baseline gap-1.5 text-[10px]">
                        <span className="text-slate-500 shrink-0">{r.label}:</span>
                        {r.vals.map((v, vi) => (
                          <span key={vi}
                            className={`inline-flex items-center rounded px-1.5 py-0.5 font-medium ${r.danger ? 'bg-rose-100 text-rose-700' : 'bg-white text-slate-700 border border-emerald-200'} ${isNew(r.field, v) ? '' : 'opacity-45'}`}>
                            {v}{isNew(r.field, v) ? '' : ' ✓'}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[9px] text-slate-500">
                    Merges into the profile — existing values are kept, only new ones are added. Faded chips (✓) are already saved. This makes future matches consistent without re-typing the prompt.
                  </p>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {matchResults && !matchLoading && (
        <div className="mt-6 space-y-4" data-testid="match-results-section">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Matched Assets
              </h2>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {matchResults.saved && matchResults.updated_at && (
                  <span className="text-[9px] text-slate-400">Last updated: {new Date(matchResults.updated_at).toLocaleDateString()}</span>
                )}
              </div>
              {matchResults._prompt_text && (
                <div className="mt-1.5 flex items-start gap-1.5 max-w-2xl" data-testid="saved-prompt-banner">
                  <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide text-violet-600 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5 mt-0.5">Prompt</span>
                  <p className="text-[11px] text-slate-600 italic leading-snug">“{matchResults._prompt_text}”</p>
                </div>
              )}
              {matchResults.company_signals && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 max-w-3xl" data-testid="company-signals-banner">
                  <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                    Company Signals · {matchResults.company_signals.company_name}
                  </span>
                  {(matchResults.company_signals.positive || []).map(a => (
                    <Badge key={`sig-pos-${a}`} variant="outline" className="text-[9px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`signal-positive-${a}`}>▲ {a}</Badge>
                  ))}
                  {(matchResults.company_signals.negative || []).map(a => (
                    <Badge key={`sig-neg-${a}`} variant="outline" className="text-[9px] px-1.5 py-0 bg-red-50 text-red-700 border-red-200 line-through" data-testid={`signal-negative-${a}`}>✕ {a}</Badge>
                  ))}
                  {(matchResults.company_signals.positive || []).length > 0 && (
                    <button
                      onClick={() => setSignalsOnly(v => !v)}
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${signalsOnly ? 'bg-amber-500 border-amber-500 text-white' : 'bg-white border-amber-300 text-amber-700 hover:bg-amber-50'}`}
                      data-testid="signals-only-toggle"
                    >
                      ⚡ Signals only · {signalMatchCount}
                      {signalsOnly && <span className="font-normal opacity-90">(hiding {(matchResults.matches || []).length - signalMatchCount} outside focus)</span>}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="text-right mr-1">
                <p className="text-2xl font-bold text-slate-900">{matchResults.total_matches}</p>
                <p className="text-[10px] text-slate-500">assets across {[...new Set(matchResults.matches.map(m => m.company_id))].length} companies</p>
              </div>
              {matchResults.matches.length > 0 && (
                <>
                  {(() => {
                    // Count distinct active-partner companies in current matches.
                    // When > 0, expose a dedicated "Pitch to {Pharma}" button
                    // that bundles all active-partner assets into a single
                    // consolidated email to the pharma contact.
                    const activePartnerCompanies = [...new Set(
                      (matchResults.matches || [])
                        .filter(m => (m.is_my_company || m.is_inbound_only) && m.company_id)
                        .map(m => m.company_id)
                    )];
                    if (activePartnerCompanies.length === 0) return null;
                    const partnerAssetCount = (matchResults.matches || []).filter(m => m.is_my_company || m.is_inbound_only).length;
                    return (
                      <Button
                        onClick={() => openActivePartnersPitch()}
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-600 text-white text-xs gap-1.5"
                        data-testid="pitch-active-partners-btn"
                      >
                        <Star className="w-3 h-3 fill-white" />
                        Pitch Active Partners to {contact?.name?.split(' ')[0] || 'Contact'}
                        <Badge className="bg-white/20 text-white text-[9px] ml-0.5">
                          {partnerAssetCount} asset{partnerAssetCount === 1 ? '' : 's'}
                        </Badge>
                      </Button>
                    );
                  })()}
                  {matchResults.matches.some(m => (m.is_new || !m.emailed) && !m.emailed_history) && (
                    <Button onClick={() => handleOpenIntroEmail(true)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1.5" data-testid="email-new-biotechs-btn">
                      <Send className="w-3 h-3" />Email Unsent
                      <Badge className="bg-white/20 text-white text-[9px] ml-0.5">{[...new Set(matchResults.matches.filter(m => (m.is_new || !m.emailed) && !m.emailed_history).map(m => m.company_id))].length} cos</Badge>
                    </Button>
                  )}
                  <Button onClick={() => handleOpenIntroEmail(false)} size="sm" variant="outline" className="text-xs gap-1.5" data-testid="email-all-biotechs-btn">
                    <Send className="w-3 h-3" />Email All
                  </Button>
                  <Button onClick={handleViewSentCampaigns} size="sm" variant="ghost" className="text-xs gap-1.5" data-testid="view-sent-campaigns-btn">
                    <FileText className="w-3 h-3" />View Sent
                  </Button>
                </>
              )}
            </div>
          </div>

          {matchResults.message && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-700">{matchResults.message}</p>
            </div>
          )}

          {matchResults.matches.length > 0 ? (
            <Card className="border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <Table data-testid="match-results-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 w-[40px]">#</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Asset</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Company</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Stage</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Indication</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Target</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Modality</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Status</TableHead>
                      <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedMatches.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="py-6 text-center text-xs text-slate-500" data-testid="signals-only-empty-row">
                          All {matchResults.matches.length} matches are outside the signalled focus — turn off “Signals only” to view them.
                        </TableCell>
                      </TableRow>
                    )}
                    {displayedMatches.map((m, i) => (
                      <TableRow key={i} className="hover:bg-slate-50/50">
                        <TableCell className="py-2 text-xs text-slate-400 w-[40px]">{i + 1}</TableCell>
                        <TableCell className="py-2 text-xs font-medium text-slate-800">
                          {m.asset_name || '—'}
                          {(m.signal_boost || []).length > 0 && (
                            <Badge variant="outline" className="ml-1.5 text-[8px] px-1 py-0 bg-amber-50 text-amber-700 border-amber-300 align-middle" title={`Matches company signal: ${m.signal_boost.join(', ')}`} data-testid={`signal-boost-${m.company_id}-${m.asset_id || m.asset_name}`}>
                              ⚡ {m.signal_boost.join(' · ')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="py-2">
                          <div className="flex items-center gap-1">
                            {m.is_my_company && <Star className="w-3 h-3 fill-amber-400 text-amber-400" />}
                            {m.is_inbound_only && <Bookmark className="w-3 h-3 fill-orange-500 text-orange-500" />}
                            <span className="text-xs text-slate-700">{m.company_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="py-2">
                          <Badge className="text-[9px] bg-sky-50 text-sky-700 border border-sky-200">{m.stage}</Badge>
                        </TableCell>
                        <TableCell className="py-2 text-[11px] text-slate-600">{m.indication || '—'}</TableCell>
                        <TableCell className="py-2 text-[11px] text-slate-600">{m.target || '—'}</TableCell>
                        <TableCell className="py-2 text-[11px] text-slate-600">{m.modality || '—'}</TableCell>
                        <TableCell className="py-2">
                          {m.is_new && <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-200">New</Badge>}
                          {m.emailed && <Badge className="text-[9px] bg-slate-100 text-slate-500 border border-slate-200">Emailed</Badge>}
                          {!m.emailed && m.emailed_history && (
                            <Badge className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200" title="You've previously emailed this contact about this biotech via another flow (AI Mapping, Bulk Email, or one-off Gmail send)" data-testid={`emailed-history-badge-${m.company_id}`}>Pitched before</Badge>
                          )}
                        </TableCell>
                        <TableCell className="py-2">
                          <div className="flex items-center gap-1.5">
                            {!m.emailed && !m.emailed_history && (
                              <button
                                onClick={() => handleOpenIntroEmail(false, [m])}
                                className="text-emerald-600 hover:text-emerald-800 p-0.5 rounded hover:bg-emerald-50 transition-colors"
                                title={`Send introduction email about ${m.asset_name || 'this asset'} to ${contact?.name || 'this contact'}`}
                                data-testid={`send-single-${m.company_id}-${m.asset_id || m.asset_name}`}
                              >
                                <Send className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <a href={`/biotech/${m.company_id}`} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:text-sky-700" title="Open biotech detail">
                              <ArrowRight className="w-3.5 h-3.5" />
                            </a>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          ) : (
            <Card className="border border-slate-200">
              <CardContent className="py-8 text-center">
                <FlaskConical className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No matching assets found</p>
                <p className="text-xs text-slate-400 mt-1">Try broadening preferred stages or therapeutic areas</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-contact-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Edit Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-slate-500">Full Name *</Label>
              <Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="edit-name" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Email</Label>
              <Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="edit-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Phone</Label>
                <Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Designation</Label>
                <Input value={form.designation || ''} onChange={e => setForm({ ...form, designation: e.target.value })} data-testid="edit-designation" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500 flex items-center gap-1.5">
                <Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" /> LinkedIn URL
              </Label>
              <Input
                value={form.linkedin_url || ''}
                onChange={e => setForm({ ...form, linkedin_url: e.target.value })}
                placeholder="https://www.linkedin.com/in/…"
                data-testid="edit-linkedin"
              />
            </div>
            <div className="relative">
              <Label className="text-xs text-slate-500">Company Name</Label>
              <Input
                value={form.company_name || ''}
                onChange={e => handleCompanyNameChange(e.target.value)}
                onFocus={() => { if (form.company_name) handleCompanyNameChange(form.company_name); }}
                onBlur={() => setTimeout(() => setShowCompanySuggestions(false), 200)}
                placeholder="Type to search or enter a new company..."
                data-testid="edit-company-name"
              />
              {showCompanySuggestions && companySuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="company-suggestions">
                  {companySuggestions.map((name, i) => (
                    <button
                      key={i}
                      type="button"
                      onMouseDown={() => selectCompanyName(name)}
                      className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-violet-50 hover:text-violet-700 transition-colors"
                      data-testid={`company-suggestion-${i}`}
                    >
                      <Building className="w-3 h-3 inline mr-1.5 text-slate-400" />{name}
                    </button>
                  ))}
                  {form.company_name.trim() && !companySuggestions.some(n => n.toLowerCase() === form.company_name.trim().toLowerCase()) && (
                    <div className="px-3 py-2 text-[10px] text-emerald-600 border-t border-slate-100 bg-emerald-50/50">
                      + New company "{form.company_name.trim()}" will be created on save
                    </div>
                  )}
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-1">Changing this will update all linked outreach records and create a new company profile if needed.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Company Type</Label>
                <Select value={form.company_type || 'Big Pharma'} onValueChange={v => setForm({ ...form, company_type: v })}>
                  <SelectTrigger data-testid="edit-company-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COMPANY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Country</Label>
                <CountrySelect value={form.location || ''} onChange={v => setForm({ ...form, location: v })} testId="edit-location" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Department</Label>
              <Input value={form.department || ''} onChange={e => setForm({ ...form, department: e.target.value })} data-testid="edit-department" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Therapeutic Areas</Label>
              <TherapeuticAreaSelect
                value={form.interest_areas || []}
                onChange={(v) => {
                  // When a TA is removed, clean up its disease_areas entry
                  // so we don't leave orphaned sub-data behind.
                  const removed = (form.interest_areas || []).filter(t => !v.includes(t));
                  let nextDA = { ...(form.disease_areas || {}) };
                  removed.forEach(t => { delete nextDA[t]; });
                  setForm({ ...form, interest_areas: v, disease_areas: nextDA });
                }}
                testId="edit-therapeutic-areas"
              />
              {(form.interest_areas || []).length > 0 && (
                <div className="mt-3 space-y-2 pl-3 border-l-2 border-slate-100">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">
                    Disease areas (optional — leave empty to mean "all diseases in this TA")
                  </p>
                  {(form.interest_areas || []).map((ta) => (
                    <div key={ta} className="flex items-start gap-2">
                      <Badge variant="secondary" className="text-[11px] px-2 py-0.5 mt-0.5 shrink-0">{ta}</Badge>
                      <DiseaseAreaSelect
                        therapeuticArea={ta}
                        value={(form.disease_areas || {})[ta] || []}
                        onChange={(v) => setForm({
                          ...form,
                          disease_areas: { ...(form.disease_areas || {}), [ta]: v }
                        })}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs text-rose-500">Excluded Therapeutic Areas</Label>
              <TherapeuticAreaSelect
                value={form.excluded_therapeutic_areas || []}
                onChange={v => setForm({ ...form, excluded_therapeutic_areas: v })}
                testId="edit-excluded-therapeutic-areas"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Preferred Stages</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {['Drug Discovery', 'Pre-Clinical', 'IND', 'Phase 1', 'Phase 2', 'Phase 3', 'Commercial'].map(stage => (
                  <button
                    key={stage}
                    type="button"
                    onClick={() => setForm(p => ({
                      ...p,
                      preferred_stages: (p.preferred_stages || []).includes(stage)
                        ? p.preferred_stages.filter(s => s !== stage)
                        : [...(p.preferred_stages || []), stage]
                    }))}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      (form.preferred_stages || []).includes(stage)
                        ? 'bg-sky-100 border-sky-300 text-sky-700 font-medium'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                    data-testid={`edit-stage-${stage.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {stage}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Preferred Modalities</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {['Small molecule', 'Monoclonal antibody', 'Bispecific / multispecific antibody', 'T-cell engagers (TCEs)', 'ADC', 'Antibody fragment / nanobody', 'Fusion protein', 'Recombinant protein / biologic', 'Enzyme replacement therapy', 'Peptide', 'siRNA', 'Antisense oligonucleotide', 'mRNA', 'mRNA-LNP', 'DNA / plasmid therapy', 'Gene therapy', 'AAV gene therapy', 'Genome editing therapy', 'Cell therapy', 'CAR-T / engineered cell therapy', 'Vaccine', 'Radiopharmaceutical', 'Microbiome therapeutic', 'Oncolytic virus', 'Regenerative medicine / tissue engineered product', 'Nanomedicine', 'Combination product'].map(mod => (
                  <button
                    key={mod}
                    type="button"
                    onClick={() => setForm(p => ({
                      ...p,
                      preferred_modalities: (p.preferred_modalities || []).includes(mod)
                        ? p.preferred_modalities.filter(m => m !== mod)
                        : [...(p.preferred_modalities || []), mod]
                    }))}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      (form.preferred_modalities || []).includes(mod)
                        ? 'bg-violet-100 border-violet-300 text-violet-700 font-medium'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                    data-testid={`edit-modality-${mod.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {mod}
                  </button>
                ))}
              </div>
            </div>
            {/* Aug 2026 · Editable granular fields — Specific Indications,
                Priority Targets/MOAs, Scientific Preferences, Excluded
                Modalities, Excluded Indications. All rendered as a common
                free-form chip editor since these are open-ended vocabularies
                that grow with the LLM tagger's output over time. */}
            <ChipListField
              label="Specific Indications"
              hint="Granular disease names — NSCLC, Multiple Myeloma, Rheumatoid Arthritis, IPF, Wet AMD, Prurigo Nodularis…"
              value={form.therapeutic_areas_specific || []}
              onChange={(v) => setForm({ ...form, therapeutic_areas_specific: v })}
              chipClass="bg-indigo-50 text-indigo-700 border-indigo-200"
              testId="edit-therapeutic-areas-specific"
            />
            <ChipListField
              label="Priority Targets / MOAs"
              hint="Specific targets or MOAs — BCMA-targeting trispecific, KATP channel, GLP-1 alt, molecular glue…"
              value={form.priority_targets_moas || []}
              onChange={(v) => setForm({ ...form, priority_targets_moas: v })}
              chipClass="bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200"
              testId="edit-priority-targets-moas"
            />
            <ChipListField
              label="Scientific Preferences"
              hint="Phrases the contact/deck uses — first-in-class, de-risked biology, human validation, pilot BE completed…"
              value={form.scientific_preferences || []}
              onChange={(v) => setForm({ ...form, scientific_preferences: v })}
              chipClass="bg-teal-50 text-teal-700 border-teal-200"
              testId="edit-scientific-preferences"
            />
            <ChipListField
              label="Focus Notes"
              hint="Residual colour that fits no other field — ex-China origination, prefers co-development…"
              value={form.focus_notes || []}
              onChange={(v) => setForm({ ...form, focus_notes: v })}
              chipClass="bg-slate-50 text-slate-700 border-slate-300"
              testId="edit-focus-notes"
            />
            <ChipListField
              label="Excluded Modalities"
              labelClass="text-rose-500"
              hint="Modalities explicitly out of scope — Autologous cell therapy, Allogeneic cell, Radiopharmaceutical, Oncolytic virus…"
              value={form.excluded_modalities || []}
              onChange={(v) => setForm({ ...form, excluded_modalities: v })}
              chipClass="bg-rose-50 text-rose-700 border-rose-200"
              testId="edit-excluded-modalities"
            />
            <ChipListField
              label="Excluded Indications"
              labelClass="text-rose-500"
              hint="Specific indications the contact does not pursue"
              value={form.excluded_indications || []}
              onChange={(v) => setForm({ ...form, excluded_indications: v })}
              chipClass="bg-rose-50 text-rose-700 border-rose-200"
              testId="edit-excluded-indications"
            />
            {/* Corporate Presentation Upload */}
            <div>
              <Label className="text-xs text-slate-500">Corporate Presentation</Label>
              <div className="mt-1.5 space-y-2">
                {(contact?.corporate_decks || []).map((deck, i) => (
                  <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2.5">
                    <FileText className="w-4 h-4 text-violet-500 shrink-0" />
                    <span className="text-xs text-slate-700 flex-1 truncate">{deck.filename}</span>
                    <button onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/pharma-contacts/${contact.id}/download-deck/${encodeURIComponent(deck.filename)}`, { responseType: 'blob', withCredentials: true });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = deck.filename;
                        link.click();
                        window.URL.revokeObjectURL(url);
                      } catch { toast.error('Failed to download file'); }
                    }} className="text-[10px] text-sky-600 hover:underline shrink-0 cursor-pointer">Download</button>
                    <button type="button" onClick={() => handleDeleteDeck(deck.filename)} className="text-slate-400 hover:text-rose-500" data-testid={`edit-deck-delete-${i}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-lg p-3 cursor-pointer hover:border-violet-300 hover:bg-violet-50/30 transition-colors">
                  <input type="file" accept=".pdf,.ppt,.pptx" className="hidden" onChange={handleDeckUpload} disabled={deckUploading} data-testid="edit-deck-upload-input" />
                  {deckUploading ? <Loader2 className="w-4 h-4 animate-spin text-violet-500" /> : <Upload className="w-4 h-4 text-slate-400" />}
                  <span className="text-xs text-slate-500">{deckUploading ? 'Uploading...' : 'Upload PDF, PPT, or PPTX'}</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="save-contact-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pitch Active Partners — single consolidated email to the pharma contact */}
      <Dialog open={pitchOpen} onOpenChange={(open) => { if (!pitchSending) setPitchOpen(open); }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="pitch-active-partners-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Star className="w-4 h-4 fill-amber-400 text-amber-500" />
              Pitch Active Partners — Single Email
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              One consolidated email to <span className="font-semibold text-slate-700">{contact?.name}</span> ({contact?.email}) covering {pitchAssets.length} asset{pitchAssets.length === 1 ? '' : 's'} from your active-partner biotechs. Each asset will also be logged as an outreach row under the corresponding biotech account.
            </p>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {groupOverlap && (
              <div className="rounded-lg border border-orange-300 bg-orange-50 p-3" data-testid="group-overlap-warning">
                <p className="text-xs font-semibold text-orange-800 mb-1">{groupOverlap.group} overlap detected</p>
                <p className="text-[11px] text-orange-700">{groupOverlap.message}</p>
                {(groupOverlap.prior_recipients || []).length > 0 && (
                  <details className="mt-1.5">
                    <summary className="text-[11px] font-medium text-orange-800 cursor-pointer" data-testid="review-recipients-toggle">Review recipients</summary>
                    <ul className="mt-1 space-y-0.5">
                      {groupOverlap.prior_recipients.map((p, i) => (
                        <li key={i} className="text-[11px] text-orange-700">
                          {p.contact_name} ({p.contact_email}) — {p.company_name}
                          {(p.assets || []).length > 0 && <span className="text-orange-500"> · {p.assets.slice(0, 4).join(', ')}</span>}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 max-h-44 overflow-y-auto" data-testid="pitch-asset-list">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-2">Assets included ({pitchAssets.length})</p>
              <ul className="space-y-1">
                {pitchAssets.map((a, i) => (
                  <li key={`${a.company_id}-${a.asset_name}-${i}`} className="text-xs text-slate-700 flex items-start gap-1.5">
                    <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400 shrink-0 mt-0.5" />
                    <span className="font-medium">{a.asset_name}</span>
                    <span className="text-slate-500">— {a.company_name}</span>
                    {a.stage && <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5">{a.stage}</Badge>}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input
                value={pitchSubject}
                onChange={(e) => setPitchSubject(e.target.value)}
                disabled={pitchSending}
                className="mt-1 text-sm"
                data-testid="pitch-subject-input"
              />
            </div>
            <div>
              <Label className="text-xs">Email body</Label>
              <Textarea
                value={pitchBody}
                onChange={(e) => setPitchBody(e.target.value)}
                disabled={pitchSending}
                className="mt-1 text-xs font-mono min-h-[260px]"
                data-testid="pitch-body-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setPitchOpen(false)} disabled={pitchSending} data-testid="pitch-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => sendActivePartnersPitch({ test: true })}
              disabled={pitchSending || !pitchSubject.trim() || !pitchBody.trim()}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              title="Sends to your own Gmail. No outreach rows are logged."
              data-testid="pitch-send-test-btn"
            >
              {pitchSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Test (to me)
            </Button>
            <Button
              onClick={() => sendActivePartnersPitch({ test: false })}
              disabled={pitchSending || !pitchSubject.trim() || !pitchBody.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="pitch-send-btn"
            >
              {pitchSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {pitchSending ? 'Sending…' : `Send to ${contact?.name?.split(' ')[0] || 'contact'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Introduction Email Dialog */}
      <Dialog open={introOpen} onOpenChange={(open) => { if (!introSending) setIntroOpen(open); }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="intro-email-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Email Biotech Companies
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              {(() => {
                const targets = introEmailTargets.length ? introEmailTargets : (matchResults?.matches || []);
                const uniqueCompanies = [...new Set(targets.map(m => m.company_name))];
                const activePartnerIds = [...new Set(targets.filter(m => m.is_my_company).map(m => m.company_id))];
                const optedIn = activePartnerIds.filter(cid => includedPartnerIds.has(cid)).length;
                const skipped = activePartnerIds.length - optedIn;
                const willSend = uniqueCompanies.length - skipped;
                if (activePartnerIds.length > 0) {
                  return (
                    <>
                      Will send to <span className="font-semibold text-slate-700">{willSend}</span> biotech{willSend !== 1 ? 's' : ''}
                      {skipped > 0 && <span className="text-amber-600"> ({skipped} active partner{skipped !== 1 ? 's' : ''} excluded)</span>}
                      {optedIn > 0 && <span className="text-emerald-600"> ({optedIn} partner{optedIn !== 1 ? 's' : ''} manually included)</span>}
                      {' '}— facilitating connection with {contact?.company_name || 'pharma partner'}
                    </>
                  );
                }
                return `Send introduction emails to ${uniqueCompanies.length} biotech companies — facilitating connection with ${contact?.company_name || 'pharma partner'}`;
              })()}
            </p>
          </DialogHeader>

          {/* Progress — pinned at top when active */}
          {introProgress && (
            <BulkSendProgress
              progress={introProgress}
              testId="intro-progress"
              onDone={() => { setIntroProgress(null); setIntroOpen(false); }}
            />
          )}
          {campaignProgress && (
            <BulkSendProgress
              progress={{
                status: campaignProgress.status,
                sent: campaignProgress.counts?.sent || 0,
                failed: campaignProgress.counts?.failed || 0,
                total: campaignProgress.counts?.planned || 0,
              }}
              testId="campaign-progress"
              onDone={() => { setCampaignProgress(null); setIntroOpen(false); }}
            />
          )}

          {/* Failure breakdown — why the failed sends failed */}
          {campaignProgress && (campaignProgress.failed_details?.length > 0) && (
            <div className="mt-2 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="campaign-failures-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-rose-700 uppercase tracking-wide">
                  {campaignProgress.failed_details.length} email{campaignProgress.failed_details.length === 1 ? '' : 's'} failed — reasons
                </div>
                {campaignProgress.counts?.failed > 0 && (
                  <button onClick={handleRetryFailed} disabled={introSending}
                    className="text-[11px] font-semibold text-rose-700 border border-rose-300 rounded-full px-2.5 py-0.5 hover:bg-rose-100 disabled:opacity-50"
                    data-testid="retry-failed-btn">
                    Retry failed
                  </button>
                )}
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1.5">
                {(() => {
                  const humanize = (err) => {
                    const e = (err || '').toLowerCase();
                    if (!err) return 'No recipient email address on file for this company.';
                    if (e.includes('routingpolicy') || e.includes('single_poc') || e.includes('routing policy')) return 'Blocked by single-point-of-contact routing policy (recipient is not the designated POC).';
                    if (e.includes('invalid to') || e.includes('invalid recipient') || e.includes('invalid email') || e.includes('address')) return 'Invalid or malformed recipient email address.';
                    if (e.includes('not connected') || e.includes('token') || e.includes('unauthor') || e.includes('401') || e.includes('credential')) return 'Gmail account not connected / authorization expired — reconnect Gmail and retry.';
                    if (e.includes('rate limit') || e.includes('429') || e.includes('quota') || e.includes('limit exceeded')) return 'Gmail sending rate/quota limit hit — retry after a short wait.';
                    if (e.includes('timeout') || e.includes('503') || e.includes('502') || e.includes('gateway') || e.includes('unavailable')) return 'Temporary Gmail/network error — safe to retry.';
                    return err;
                  };
                  const groups = {};
                  campaignProgress.failed_details.forEach((f) => {
                    const reason = humanize(f.error);
                    (groups[reason] = groups[reason] || []).push(f);
                  });
                  return Object.entries(groups).map(([reason, items]) => (
                    <div key={reason} className="bg-white border border-rose-100 rounded p-2">
                      <div className="text-[11px] font-semibold text-rose-800">{reason} <span className="text-rose-400 font-normal">· {items.length}</span></div>
                      <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                        {items.map((f) => f.company_name || f.contact_email || f.biotech_company_id).filter(Boolean).join(', ')}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Campaign readiness panel — pre-send breakdown */}
          {campaignPlanLoading && (
            <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-500 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Checking outreach history for duplicates…
            </div>
          )}
          {campaignPlan && !campaignPlanLoading && (
            <div className="mt-2 bg-gradient-to-br from-sky-50 to-emerald-50 border-2 border-sky-200 rounded-lg p-4" data-testid="campaign-readiness-panel">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
                    Campaign Readiness
                    {campaignPlan.pharma_name && (
                      <span className="ml-1.5 text-slate-500 normal-case font-normal tracking-normal">
                        — dedup scoped to <span className="font-semibold text-slate-700">{campaignPlan.pharma_name}</span>
                        {(campaignPlan.pharma_names_checked?.length || 0) > 1 && (
                          <span className="text-slate-400"> +{campaignPlan.pharma_names_checked.length - 1} alias</span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="bg-white border border-emerald-200 rounded px-3 py-1.5">
                      <div className="text-[10px] text-slate-500 uppercase tracking-wide">Remaining biotechs</div>
                      <div className="text-lg font-bold text-emerald-700" data-testid="plan-remaining">{campaignPlan.counts.remaining}</div>
                    </div>
                    {typeof campaignPlan.counts.remaining_pairs === 'number' && (
                      <div className="bg-white border border-emerald-200 rounded px-3 py-1.5" title="Number of (biotech × asset) pitches that will actually go out. Multiple assets to the same biotech go in one email.">
                        <div className="text-[10px] text-slate-500 uppercase tracking-wide">Asset pitches to send</div>
                        <div className="text-lg font-bold text-emerald-700" data-testid="plan-remaining-pairs">{campaignPlan.counts.remaining_pairs}</div>
                      </div>
                    )}
                    {campaignPlan.counts.already_sent > 0 && (
                      <div className="bg-white border border-slate-200 rounded px-3 py-1.5" title="These biotechs will be automatically skipped — every asset the campaign would pitch has already been sent to this pharma company (via any contact).">
                        <div className="text-[10px] text-slate-500 uppercase tracking-wide">Fully deduped</div>
                        <div className="text-lg font-bold text-slate-600" data-testid="plan-already-sent">{campaignPlan.counts.already_sent}</div>
                      </div>
                    )}
                    {campaignPlan.counts.partial_biotechs > 0 && (
                      <div className="bg-white border border-indigo-200 rounded px-3 py-1.5" title="These biotechs will still get an email — but only the assets that haven't been pitched to this pharma before. Assets already pitched are silently dropped.">
                        <div className="text-[10px] text-slate-500 uppercase tracking-wide">Partial (asset dedup)</div>
                        <div className="text-lg font-bold text-indigo-700" data-testid="plan-partial">{campaignPlan.counts.partial_biotechs}</div>
                      </div>
                    )}
                    {campaignPlan.counts.previously_failed > 0 && (
                      <div className="bg-white border border-amber-200 rounded px-3 py-1.5" title="Previous outreach to these biotechs failed — they will be re-attempted.">
                        <div className="text-[10px] text-slate-500 uppercase tracking-wide">Previously failed</div>
                        <div className="text-lg font-bold text-amber-700" data-testid="plan-prev-failed">{campaignPlan.counts.previously_failed}</div>
                      </div>
                    )}
                    {typeof campaignPlan.counts.already_sent_pairs === 'number' && campaignPlan.counts.already_sent_pairs > 0 && (
                      <div className="bg-white border border-slate-200 rounded px-3 py-1.5" title="Total (biotech × asset) pitches already sent to this pharma company via any contact.">
                        <div className="text-[10px] text-slate-500 uppercase tracking-wide">Prior pitches (any contact)</div>
                        <div className="text-lg font-bold text-slate-500" data-testid="plan-already-sent-pairs">{campaignPlan.counts.already_sent_pairs}</div>
                      </div>
                    )}
                  </div>
                </div>
                {campaignPlan.interrupted_campaign_id && (
                  <div className="bg-amber-100 border-2 border-amber-400 rounded-lg p-3 flex items-center gap-3">
                    <div className="text-2xl">⏸</div>
                    <div className="flex-1">
                      <div className="text-xs font-bold text-amber-900">Interrupted campaign detected</div>
                      <div className="text-[11px] text-amber-800 mt-0.5">
                        {campaignPlan.interrupted_remaining} biotech{campaignPlan.interrupted_remaining === 1 ? '' : 's'} still to send from a previous run.
                      </div>
                      <Button size="sm" onClick={handleResumeInterrupted} disabled={introSending} className="mt-2 bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs" data-testid="resume-interrupted-btn">
                        Resume Campaign
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {campaignPlan.counts.remaining === 0 && !campaignPlan.interrupted_campaign_id && (
                <div className="mt-3 text-xs text-slate-600 bg-white border border-slate-200 rounded p-2">
                  🛡️ Every matched biotech has already received an outreach for this contact. Nothing to send.
                </div>
              )}
            </div>
          )}

          <div className="space-y-4 mt-2">
            {(() => {
              const targets = introEmailTargets.length ? introEmailTargets : (matchResults?.matches || []);
              const dncMap = new Map();
              targets.filter(m => m.do_not_contact?.flagged).forEach(m => {
                if (m.company_id && !dncMap.has(m.company_id)) {
                  dncMap.set(m.company_id, { name: m.company_name, reason: m.do_not_contact?.reason || '' });
                }
              });
              const dncList = Array.from(dncMap.values());
              if (dncList.length === 0) return null;
              return (
                <div className="bg-rose-50 border-2 border-rose-300 rounded-lg p-3" data-testid="do-not-contact-banner">
                  <div className="flex items-start gap-2">
                    <div className="shrink-0 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center text-sm">⛔</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-rose-900 mb-1">
                        {dncList.length} biotech{dncList.length !== 1 ? 's' : ''} flagged "Do Not Contact" — auto-blocked
                      </p>
                      <p className="text-[11px] text-rose-800 mb-2 leading-relaxed">
                        These companies have asked us not to reach out (e.g. "we don't engage intermediaries"). They are <span className="font-semibold">permanently excluded</span> — no checkbox to opt them back in. Remove the flag on their company page if their stance changes.
                      </p>
                      <ul className="space-y-1 bg-white/70 border border-rose-200 rounded-md p-2 max-h-40 overflow-y-auto">
                        {dncList.map((d, i) => (
                          <li key={i} className="text-xs text-rose-900" data-testid={`dnc-row-${i}`}>
                            <span className="font-medium">⛔ {d.name}</span>
                            {d.reason && <span className="italic text-rose-600"> — {d.reason}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}
            {(() => {
              const targets = introEmailTargets.length ? introEmailTargets : (matchResults?.matches || []);
              const activePartnerMap = new Map();
              targets.filter(m => m.is_my_company && m.company_id).forEach(m => {
                if (!activePartnerMap.has(m.company_id)) {
                  activePartnerMap.set(m.company_id, { id: m.company_id, name: m.company_name });
                }
              });
              const activePartners = Array.from(activePartnerMap.values());
              if (activePartners.length === 0) return null;
              const optedInCount = activePartners.filter(p => includedPartnerIds.has(p.id)).length;
              const toggleAll = () => {
                if (optedInCount === activePartners.length) {
                  setIncludedPartnerIds(new Set());
                } else {
                  setIncludedPartnerIds(new Set(activePartners.map(p => p.id)));
                }
              };
              const toggleOne = (id) => {
                setIncludedPartnerIds(prev => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id); else next.add(id);
                  return next;
                });
              };
              return (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3" data-testid="active-partners-selection">
                  <div className="flex items-start gap-2">
                    <div className="shrink-0 w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center text-sm">⭐</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-xs font-semibold text-amber-900">
                          {activePartners.length} active partner{activePartners.length !== 1 ? 's' : ''} in this list
                        </p>
                        <button
                          type="button"
                          onClick={toggleAll}
                          className="text-[10px] font-medium text-amber-700 hover:text-amber-900 underline"
                          data-testid="toggle-all-active-partners"
                        >
                          {optedInCount === activePartners.length ? 'Uncheck all' : 'Check all to include'}
                        </button>
                      </div>
                      <p className="text-[11px] text-amber-800 leading-relaxed mb-2">
                        These are flagged as <span className="font-semibold">"My Companies"</span> (existing relationships) — unchecked by default. <span className="font-semibold">Check the ones you want to include</span> in this send:
                      </p>
                      {/* Shortcut: instead of including the active partners
                          in this outbound-to-biotechs flow, jump straight to
                          the single-email-to-pharma pitch flow. */}
                      <div className="bg-white border border-amber-300 rounded-md p-2 mb-2 flex items-center justify-between gap-2">
                        <p className="text-[11px] text-amber-900 leading-snug flex-1">
                          <span className="font-semibold">Recommended:</span> send <span className="font-semibold">one consolidated email to {contact?.name?.split(' ')[0] || 'the contact'}</span> covering all {activePartners.length} active partner{activePartners.length !== 1 ? 's' : ''} instead — no need to email each biotech individually.
                        </p>
                        <Button
                          size="sm"
                          onClick={() => { setIntroOpen(false); setTimeout(() => openActivePartnersPitch(), 100); }}
                          className="bg-amber-500 hover:bg-amber-600 text-white text-[11px] gap-1 h-7 px-2.5 shrink-0"
                          data-testid="pitch-active-partners-from-modal"
                        >
                          <Star className="w-3 h-3 fill-white" /> Pitch to {contact?.name?.split(' ')[0] || 'contact'}
                        </Button>
                      </div>
                      <div className="space-y-1 max-h-48 overflow-y-auto bg-white/70 border border-amber-200 rounded-md p-2">
                        {activePartners.map((p) => {
                          const checked = includedPartnerIds.has(p.id);
                          return (
                            <label key={p.id} className="flex items-center gap-2 cursor-pointer hover:bg-amber-50 rounded px-2 py-1 transition-colors" data-testid={`active-partner-toggle-${p.id}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleOne(p.id)}
                                disabled={introSending}
                                className="w-3.5 h-3.5 rounded border-amber-400 text-amber-600 focus:ring-amber-500 cursor-pointer"
                              />
                              <span className="text-amber-400 text-xs">⭐</span>
                              <span className={`text-xs ${checked ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>{p.name}</span>
                              {checked && <Badge className="bg-emerald-100 text-emerald-700 text-[9px] ml-auto">Will be included</Badge>}
                              {!checked && <Badge className="bg-slate-100 text-slate-500 text-[9px] ml-auto">Skipped</Badge>}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Recipient routing preview — confirms 1 email per biotech to the
                BD-aware primary contact, lists who will receive each email and
                why, plus the contacts that were skipped at that biotech. The
                user explicitly asked for this confirmation before sending. */}
            {introRecipientPreview && (
              <div className="bg-sky-50 border border-sky-200 rounded-lg" data-testid="intro-recipient-preview">
                <button
                  type="button"
                  onClick={() => setIntroRecipientExpanded(v => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left"
                  data-testid="intro-recipient-toggle"
                >
                  <div className="flex items-center gap-2 text-[12px] text-sky-900">
                    <Mail className="w-3.5 h-3.5 text-sky-600 shrink-0" />
                    <span className="font-semibold">{introRecipientPreview.ready} email{introRecipientPreview.ready !== 1 ? 's' : ''}</span>
                    <span>— one per biotech, to the BD/licensing primary contact only (no CC).</span>
                    {introRecipientPreview.skipped > 0 && (
                      <span className="text-amber-700">· {introRecipientPreview.skipped} skipped</span>
                    )}
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-sky-600 transition-transform ${introRecipientExpanded ? 'rotate-180' : ''}`} />
                </button>
                {introRecipientExpanded && (
                  <div className="border-t border-sky-200 max-h-60 overflow-y-auto bg-white/70">
                    {(introRecipientPreview.recipients || []).map((r, i) => (
                      <div
                        key={r.company_id || i}
                        className={`px-3 py-1.5 text-[11px] border-b border-slate-100 last:border-b-0 ${r.status === 'skipped' ? 'opacity-60' : ''}`}
                        data-testid={`intro-recipient-row-${r.company_id}`}
                      >
                        <div className="flex items-baseline gap-2">
                          <span className="font-medium text-slate-800 truncate">{r.company_name}</span>
                          {r.status === 'skipped' ? (
                            <span className="text-amber-700">— skipped: {r.skip_reason}</span>
                          ) : (
                            <>
                              <span className="text-slate-400">→</span>
                              <span className="text-slate-700 font-medium">{r.contact_name || '(unnamed)'}</span>
                              {r.designation && <span className="text-slate-500">· {r.designation}</span>}
                              {r.contact_email && <span className="text-slate-400">· {r.contact_email}</span>}
                            </>
                          )}
                        </div>
                        {r.status === 'ready' && r.primary_reason && (
                          <div className="text-[10px] text-slate-400 italic ml-1">picked because: {r.primary_reason}</div>
                        )}
                        {r.status === 'ready' && r.siblings_skipped && r.siblings_skipped.length > 0 && (
                          <div className="text-[10px] text-slate-400 italic ml-1">
                            (not contacted at this biotech: {r.siblings_skipped.slice(0, 4).join(', ')}{r.siblings_skipped.length > 4 ? `, +${r.siblings_skipped.length - 4} more` : ''})
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {introRecipientLoading && !introRecipientPreview && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[11px] text-slate-500 flex items-center gap-2" data-testid="intro-recipient-loading">
                <Loader2 className="w-3 h-3 animate-spin" /> Resolving primary contact per biotech…
              </div>
            )}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2">
              <Mail className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-[11px] text-amber-700">
                Placeholders auto-replaced per email: <code className="bg-amber-100 px-1 rounded">[Name]</code> recipient first name, <code className="bg-amber-100 px-1 rounded">[Company]</code> biotech, <code className="bg-amber-100 px-1 rounded">[Asset]</code>, <code className="bg-amber-100 px-1 rounded">[TherapeuticArea]</code>, <code className="bg-amber-100 px-1 rounded">[Indication]</code>, <code className="bg-amber-100 px-1 rounded">[Stage]</code>.
                {pharmaBriefLoading && <span className="ml-1 italic">Loading pharma brief...</span>}
              </p>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Subject</Label>
              <Input value={introSubject} onChange={e => setIntroSubject(e.target.value)} placeholder="Subject line..." disabled={introSending} data-testid="intro-subject" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-slate-600">Email Body</Label>
                <Button onClick={handleGenerateAIEmail} disabled={introGenerating || introSending} variant="outline" size="sm" className="h-6 text-[10px] gap-1" data-testid="ai-generate-btn">
                  {introGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <FlaskConical className="w-3 h-3" />}
                  AI Generate
                </Button>
              </div>
              <textarea
                value={introBody}
                onChange={e => { setIntroBody(e.target.value); setIntroPreviews(null); }}
                rows={8}
                disabled={introSending}
                className="w-full text-sm border border-slate-200 rounded-lg p-3 focus:ring-2 focus:ring-sky-200 focus:border-sky-400 outline-none resize-none disabled:opacity-60 disabled:bg-slate-50"
                placeholder="Email body..."
                data-testid="intro-body"
              />
            </div>

            {/* Test Email Results */}
            {introPreviews && introPreviews.length > 0 && (
              <div className="space-y-2.5" data-testid="test-email-section">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>
                    </div>
                    <span className="text-xs font-semibold text-emerald-800">
                      {introPreviews.filter(r => r.status === 'sent').length} test emails sent to {introTestSentTo}
                    </span>
                  </div>
                  <p className="text-[10px] text-emerald-600 ml-7">Check your inbox — each email shows [TEST] in the subject and "would go to: [recipient]"</p>
                </div>
                {introPreviews.map((r, idx) => (
                  <div key={idx} className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${r.status === 'sent' ? 'bg-white border-slate-200' : 'bg-rose-50 border-rose-200'}`} data-testid={`test-result-${idx}`}>
                    <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-800 truncate">{r.company_name}</p>
                      <p className="text-[10px] text-slate-500 truncate">To: {r.contact_name || 'Team'} &lt;{r.contact_email}&gt;</p>
                    </div>
                    {r.status === 'sent' ? (
                      <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">Sent</Badge>
                    ) : (
                      <Badge className="text-[9px] bg-rose-50 text-rose-700 border border-rose-200 shrink-0">Failed</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              {introProgress?.status === 'completed' ? (
                <Button
                  onClick={() => setIntroOpen(false)}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 px-6"
                  data-testid="intro-done-btn"
                >
                  <CheckCircle className="w-4 h-4" />
                  Done
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setIntroOpen(false)} size="sm" disabled={introSending} data-testid="intro-cancel-btn">
                    Cancel
                  </Button>
                  <Button
                    onClick={handlePreviewTop3}
                    disabled={introSending || introPreviewLoading || !introBody.trim()}
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    data-testid="send-test-btn"
                  >
                    {introPreviewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    Send 3 Test Emails
                  </Button>
                  <Button
                    onClick={handleSendIntroEmails}
                    disabled={introSending}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                    data-testid="intro-send-btn"
                  >
                    {introSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    {introSending ? 'Sending...' : 'Send to All Biotechs'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Block / Reassign dialog */}
      <Dialog open={dncDialogOpen} onOpenChange={setDncDialogOpen}>
        <DialogContent className="sm:max-w-xl" data-testid="dnc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldOff className="w-4 h-4 text-red-600" />
              Block or Reassign — {contact.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-1 text-sm">
            {/* Section 1 — Block */}
            <div className="space-y-2">
              <p className="font-semibold text-slate-800">1. Mark Do Not Contact</p>
              <p className="text-[11px] text-slate-500">
                Future bulk + per-row sends will refuse this contact. Existing outreach history stays untouched.
              </p>
              <textarea
                value={dncReason}
                onChange={(e) => setDncReason(e.target.value)}
                placeholder="Reason (optional) — e.g. 'Asked to redirect to Jesse Hwang per email on 11 May'"
                rows={2}
                className="w-full text-xs p-2 rounded-md border border-slate-300 resize-y bg-white"
                data-testid="dnc-reason-input"
              />
            </div>

            {/* Section 2 — Reassign / Redirect */}
            <div className="space-y-2 border-t border-slate-100 pt-4">
              <p className="font-semibold text-slate-800">2. Reassign outreach to another contact</p>
              <p className="text-[11px] text-slate-500">
                Move ALL of {contact.name}'s evaluations (sent / declined / awaiting) to a different contact so deal history stays intact under the new person. Optional but recommended when redirecting.
              </p>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  value={reassignTargetSearch}
                  onChange={(e) => searchReassignTargets(e.target.value)}
                  placeholder="Search by name or email…"
                  className="pl-8 h-9 text-sm"
                  data-testid="reassign-search-input"
                />
              </div>
              {reassignTargetResults.length > 0 && (
                <div className="border border-slate-200 rounded-md max-h-44 overflow-y-auto divide-y divide-slate-100 bg-white">
                  {reassignTargetResults.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => { setReassignTargetPicked(c); setReassignTargetResults([]); setReassignTargetSearch(`${c.name} <${c.email || 'no email'}>`); }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 text-xs"
                      data-testid={`reassign-pick-${c.id}`}
                    >
                      <span className="font-medium text-slate-800">{c.name}</span>
                      <span className="text-slate-400"> · {c.email || 'no email'}</span>
                      <span className="text-slate-400"> · {c.company_name}</span>
                    </button>
                  ))}
                </div>
              )}
              {reassignTargetPicked && (
                <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5">
                  Selected: <span className="font-semibold">{reassignTargetPicked.name}</span> ({reassignTargetPicked.email || 'no email'})
                </div>
              )}
              <Button
                onClick={runReassign}
                disabled={reassignRunning || !reassignTargetPicked}
                variant="outline"
                size="sm"
                className="text-xs gap-1.5"
                data-testid="run-reassign-btn"
              >
                {reassignRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                {reassignRunning ? 'Moving…' : 'Move evaluations'}
              </Button>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDncDialogOpen(false)} disabled={dncSaving} data-testid="dnc-close-btn">
              Close
            </Button>
            <Button
              onClick={() => saveDncFlag(true)}
              disabled={dncSaving}
              className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
              data-testid="dnc-save-btn"
            >
              {dncSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldOff className="w-3.5 h-3.5" />}
              {contact.do_not_contact?.flagged ? 'Update Block' : 'Block This Contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Account from this contact's preferences — confirm modal */}
      <Dialog open={createAccountOpen} onOpenChange={setCreateAccountOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0"
          data-testid="create-account-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 shrink-0">
            <DialogTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Create Account from Contact
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Spins up a new account pre-populated from {contact?.name?.split(' ')[0] || 'this contact'}'s stated priorities plus their company's MOAT-enriched profile. Tweak anything below before saving.
            </p>
          </DialogHeader>
          <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">
            <div>
              <Label className="text-xs">Account Name <span className="text-rose-500">*</span></Label>
              <Input
                value={accountForm.project_name}
                onChange={e => setAccountForm({ ...accountForm, project_name: e.target.value })}
                placeholder="e.g. Takeda Oncology In-Licensing"
                className="text-sm mt-1"
                data-testid="create-account-name-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">Auto-derived as "{`{Pharma} {Primary TA} In-Licensing`}". Edit if you want a different naming convention.</p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs text-slate-600">
              <div className="border border-slate-200 rounded px-3 py-2 bg-slate-50">
                <span className="text-slate-400 uppercase tracking-wide text-[10px]">Pharma</span>
                <div className="font-medium text-slate-800 text-sm mt-0.5">{contact?.company_name || '—'}</div>
              </div>
              <div className="border border-slate-200 rounded px-3 py-2 bg-slate-50">
                <span className="text-slate-400 uppercase tracking-wide text-[10px]">Contact</span>
                <div className="font-medium text-slate-800 text-sm mt-0.5">{contact?.name || '—'}</div>
                {contact?.designation && <div className="text-[10px] text-slate-500">{contact.designation}</div>}
              </div>
            </div>

            {/* Therapeutic Areas chips */}
            <div>
              <Label className="text-xs">Therapeutic Areas</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {accountForm.therapeutic_areas.map(ta => (
                  <Badge key={ta} variant="outline" className="text-[11px] gap-1 bg-sky-50 border-sky-200 text-sky-700">
                    {ta}
                    <button onClick={() => removeChip('therapeutic_areas', ta)} className="hover:text-rose-500" data-testid={`remove-ta-${ta.replace(/\s/g, '-')}`}>×</button>
                  </Badge>
                ))}
              </div>
              <Input
                value={taChipInput}
                onChange={e => setTaChipInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChip('therapeutic_areas', taChipInput, setTaChipInput); } }}
                placeholder="Add a TA (press Enter)"
                className="text-sm mt-2 h-8"
                data-testid="create-account-ta-input"
              />
            </div>

            {/* Diseases / Indications chips — auto-derived from TAs via the
                curated dict (LLM fallback) so the BD lead doesn't have to
                type them. Updates when TA chips change. User can still edit
                freely before saving. */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Diseases / Indications</Label>
                <span className="text-[10px] text-slate-400 italic">{diseaseLoading ? 'auto-deriving from TA…' : 'inside the picked therapeutic area(s)'}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5 min-h-[28px]" data-testid="create-account-diseases-chips">
                {(accountForm.preferred_diseases || []).map(d => (
                  <Badge key={d} variant="outline" className="text-[11px] gap-1 bg-violet-50 border-violet-200 text-violet-700">
                    {d}
                    <button onClick={() => removeChip('preferred_diseases', d)} className="hover:text-rose-500" data-testid={`remove-disease-${d.replace(/\s/g, '-').replace(/[^a-z0-9-]/gi, '')}`}>×</button>
                  </Badge>
                ))}
                {diseaseLoading && (accountForm.preferred_diseases || []).length === 0 && (
                  <span className="text-[11px] text-slate-400 italic flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> deriving disease list…
                  </span>
                )}
              </div>
              <Input
                value={diseaseChipInput}
                onChange={e => setDiseaseChipInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChip('preferred_diseases', diseaseChipInput, setDiseaseChipInput); } }}
                placeholder="Add a disease / indication (press Enter)"
                className="text-sm mt-2 h-8"
                data-testid="create-account-disease-input"
              />
            </div>

            {/* Preferred Stages chips */}
            <div>
              <Label className="text-xs">Preferred Stages</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {accountForm.preferred_stages.map(s => (
                  <Badge key={s} variant="outline" className="text-[11px] gap-1 bg-amber-50 border-amber-200 text-amber-700">
                    {s}
                    <button onClick={() => removeChip('preferred_stages', s)} className="hover:text-rose-500">×</button>
                  </Badge>
                ))}
              </div>
              <Input
                value={stageChipInput}
                onChange={e => setStageChipInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChip('preferred_stages', stageChipInput, setStageChipInput); } }}
                placeholder="e.g. Phase 2, Phase 3 (press Enter)"
                className="text-sm mt-2 h-8"
                data-testid="create-account-stage-input"
              />
            </div>

            {/* Preferred Modalities chips */}
            <div>
              <Label className="text-xs">Preferred Modalities</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {accountForm.preferred_modalities.map(m => (
                  <Badge key={m} variant="outline" className="text-[11px] gap-1 bg-violet-50 border-violet-200 text-violet-700">
                    {m}
                    <button onClick={() => removeChip('preferred_modalities', m)} className="hover:text-rose-500">×</button>
                  </Badge>
                ))}
              </div>
              <Input
                value={modalityChipInput}
                onChange={e => setModalityChipInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChip('preferred_modalities', modalityChipInput, setModalityChipInput); } }}
                placeholder="e.g. ADC, mRNA, mAb (press Enter)"
                className="text-sm mt-2 h-8"
                data-testid="create-account-modality-input"
              />
            </div>

            <div>
              <Label className="text-xs">Notes <span className="text-slate-400">(optional)</span></Label>
              <Textarea
                value={accountForm.notes}
                onChange={e => setAccountForm({ ...accountForm, notes: e.target.value })}
                placeholder="Free-text — call notes, deal context, anything worth carrying into the new account…"
                className="text-sm mt-1 min-h-[60px]"
                data-testid="create-account-notes-input"
              />
            </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-slate-100 bg-white shrink-0">
            <Button variant="outline" onClick={() => setCreateAccountOpen(false)} disabled={createAccountSaving} data-testid="create-account-cancel-btn">Cancel</Button>
            <Button
              onClick={handleCreateAccount}
              disabled={createAccountSaving || !accountForm.project_name?.trim()}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="create-account-confirm-btn"
            >
              {createAccountSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-2" />}
              {createAccountSaving ? 'Creating…' : 'Create Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Sent Campaigns — history modal */}
      <Dialog open={viewSentOpen} onOpenChange={setViewSentOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="view-sent-campaigns-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-600" />
              Campaign History · {contact?.name}
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Every intro-outreach campaign ever run for this pharma contact. State is persistent — resume any interrupted run.
            </p>
          </DialogHeader>
          {historicalCampaigns.length === 0 ? (
            <div className="py-10 text-center text-slate-400 text-sm">
              No campaigns have been run for this contact yet.
            </div>
          ) : (
            <div className="space-y-2">
              {historicalCampaigns.map((c) => {
                const st = c.status;
                const cts = c.counts || {};
                const statusStyle = st === 'completed' ? 'bg-emerald-100 text-emerald-800'
                  : st === 'interrupted' ? 'bg-amber-100 text-amber-800'
                  : st === 'running' ? 'bg-sky-100 text-sky-800 animate-pulse'
                  : 'bg-rose-100 text-rose-800';
                return (
                  <div key={c.id} className="border border-slate-200 rounded-lg p-3" data-testid={`history-campaign-${c.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-semibold text-slate-900 truncate">
                            {c.subject || '(no subject)'}
                          </div>
                          <span className={`text-[10px] uppercase tracking-wide font-bold rounded px-1.5 py-0.5 ${statusStyle}`}>
                            {st}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                          {c.created_by_email && <> · by {c.created_by_email}</>}
                        </div>
                        <div className="mt-2 flex items-center gap-3 text-xs flex-wrap">
                          <span className="text-emerald-700 font-medium">Sent {cts.sent}</span>
                          {cts.failed > 0 && <span className="text-rose-700 font-medium">Failed {cts.failed}</span>}
                          {cts.skipped > 0 && <span className="text-slate-500">Skipped {cts.skipped}</span>}
                          {cts.remaining > 0 && <span className="text-amber-700 font-medium">Remaining {cts.remaining}</span>}
                          <span className="text-slate-400">· {cts.percent_complete}% complete</span>
                        </div>
                      </div>
                      {st === 'interrupted' && (cts.remaining || 0) > 0 && (
                        <Button
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs"
                          onClick={() => { setViewSentOpen(false); handleSendIntroEmails({ resumeCampaignId: c.id }); }}
                          data-testid={`history-resume-${c.id}`}
                        >
                          Resume
                        </Button>
                      )}
                      {cts.failed > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/outreach-campaigns/${c.id}/retry-failed`, {}, { withCredentials: true });
                              toast.success(`Retrying ${cts.failed} failed row${cts.failed === 1 ? '' : 's'}…`);
                              const r = await axios.get(`${API}/pharma-contacts/${id}/campaigns`, { withCredentials: true });
                              setHistoricalCampaigns(r.data.campaigns || []);
                            } catch (e) { toast.error(errMsg(e, 'Retry failed')); }
                          }}
                          data-testid={`history-retry-${c.id}`}
                        >
                          Retry {cts.failed} failed
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex justify-end mt-3">
            <Button variant="outline" onClick={() => setViewSentOpen(false)} data-testid="close-view-sent">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

